import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { DateTime } from 'luxon';
import { FitnessPlan, Plan_SubSlot, Plan_ExercisePath, getSlotPathIntensity, getRepTargetForIntensity, getMyoRepsForIntensity, getMyoRepsForTargetRepsCount, getMyoSetsFromSets, IntensityLevel, getMuscleVolumeForPlan, PerMuscleVolume, workoutAlreadyListsExercise, isCoachSetWeight } from '@cf-shared/data-model/fitness-plan';
import { buildMuscleVolumeInputsAsync, reportOrphanSubSlotIds } from 'util/muscle-volume-helpers';
import { buildMuscleVolumeRows } from 'util/muscle-volume-rows';
import { DisplaySubSlotSource, resolveDisplaySubSlot } from 'util/historical-subslot';
import { reportDisplaySubSlotMissingFromPlan } from 'util/orphan-subslot-reporter';
import { FitnessState, DeclaredState, ExerciseStat, getWorkoutsPerWeek } from '@cf-shared/data-model/user-state';
import { adjustWeight } from '@cf-shared/data-model/equipment';
import { formatExerciseSets, Formatting, formatMeasure } from 'util/formatting';
import { Log_Workout, Log_WorkoutExercise, Log_WorkoutSet, mergeWorkoutExercisesWithPlan, healSparseLogSets, setTypeHasNoTargetPrescription, combinedEntryForExercise, isDeloadCarriedOverFromLastSession, storedExercisesWithoutHoles } from '@cf-shared/data-model/log-workout';
import { deepClone } from '@cf-shared/deep-helpers';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { SetType, PreSetStatus } from '@cf-shared/enums';
import { LocaleSettings } from '@cf-shared/shared-util';
import { ExerciseDbService, ExerciseSlot, exerciseUsesMyoReps, exerciseUsesRMTesting, isCustomExerciseById } from 'services/exercise-db.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { LoggingService } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { UiExercise, UiSet, ExerciseBadge } from 'models/workout-ui-models';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { getExerciseForSubSlot, rotateExerciseInSubSlotByApp, suggestAvailableExercise, exercisesOnSameEquipment, pickReplacementAvoidingSameEquipment, changeExerciseInWorkoutAsync, findNearestLiveAncestorAsync, ExerciseChosenBy } from 'services/exercise-switching';
import { isPouchConflict, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { decideLayoffPrescription, LayoffEvidence, LayoffOutcome } from 'services/layoff-prescription';
import { reducedTargetWeightAfterDays } from '@cf-shared/fitness-constants';
import { sumBy } from 'lodash-es';
import { daysSinceDate } from 'util/time';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { getUnitWithLocaleFallback } from 'services/equipment-helpers';
import { WorkoutLoadMode } from 'models/workout-load-mode';
import { mayHealPlanReferenceInMode, PlanMerge, planMergeForMode, resolveWorkoutLoadModeFromNeighbours, resolveWorkoutLoadModeWithoutNeighbours, WorkoutLoadModeResult } from 'services/workout-load-mode-resolve';
import { namesOfMissingWorkoutLoadingDocuments } from 'services/workout-loading-context-check';
import { bestStrengthSetAcrossSessions, capPrescribedWeight_kg, decideMaxedOutMarkerAtLoad, MaxedOutMarkerAction, PrescriptionCapResnap, resnapPrescriptionCap, sessionSetsForExercise } from 'pages/workout-detail/workout-progression-decision';
import { SetStats } from '@cf-shared/data-model/set-stats';
import { repairStaleSetFigures } from '@cf-shared/data-model/set-stats-consistency';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { decideNextPlanReference, decideOutOfRangePlanReferenceRepair, decidePlanReferenceHeal, describeEmptyLookBack, decideWorkoutStamp, earlierWorkoutReadEndUtcDay, LookBackCoverage, mayStampPlanReference, mayTrustEarliestLogDay, OutOfRangePlanReferenceRepair, PlanReferenceAnswer, PlanReferenceSource, PreviousWorkoutFacts, subSlotIdsWithAFinishedSet } from 'services/workout-plan-reference-decisions';

// re-exported so every existing `from 'services/workout-loader.service'` import
// of WorkoutLoadMode keeps working; the enum itself lives in a leaf module that
// a plain unit test can import without pulling in Angular
export { WorkoutLoadMode };
// same reason: callers of getEffectiveExercises import its argument from here
export { PlanMerge, planMergeForMode };

/**
 * Context loaded once per page for efficient reuse across multiple exercises
 */
export interface WorkoutLoadingContext {
  plan: FitnessPlan;
  fitnessState: FitnessState;
  declaredState: DeclaredState;
  nowLocal: DateTime;
  locale: LocaleSettings;
  watcher: DocWatcher;
  /** `_id` of the workout being loaded. The prescription cap (#5176) reads the user's logged
   *  history to judge what they can currently lift, and must not count the session they are in the
   *  middle of — that would let a set logged minutes ago decide the weight of the next one.
   *  Deliberately REQUIRED rather than optional: a call site that forgot it would silently read the
   *  current session as history, and nothing downstream could tell. */
  currentWorkoutId: string;
}

/**
 * Result of loading exercise - only contains flags that need to be acted upon by caller
 */
export interface LoadExerciseResult {
  // Flag indicating the exercise needs to be replaced due to max weight reached
  needsExerciseSwitch: boolean;
  // Flag indicating a corrupted plan path was fixed (needs persistence)
  pathFixupApplied: boolean;
  // Flag indicating a sparse `sets` array was healed (Issue #720)
  sparseSetsHealed: boolean;
}

/** Whether the recent-history read behind the prescription cap could look at the user's sessions. */
enum RecentHistoryRead {
  Read = 'Read',
  Unreadable = 'Unreadable',
}

/** What reading the log's ids for a workout saved before the searched run found (df-afa259234d1a). */
enum EarlierWorkoutCheck {
  /** the read was not needed, or could not prove anything on this device yet */
  NotRun = 'NotRun',
  FoundOne = 'FoundOne',
  NoneBefore = 'NoneBefore',
  ReadFailed = 'ReadFailed',
}

type EarlierWorkoutCheckResult =
  | { outcome: EarlierWorkoutCheck.NotRun | EarlierWorkoutCheck.FoundOne | EarlierWorkoutCheck.ReadFailed }
  | { outcome: EarlierWorkoutCheck.NoneBefore; noSavedWorkoutBeforeDay: string };

/** This exercise's recent workouts, newest first, or the fact that they could not be read. The
 *  layoff, the #5176 cap and the #4822 deload note all look at the same handful of sessions, so the
 *  load reads them once and shares the answer. */
type RecentWorkoutsRead =
  | { read: RecentHistoryRead.Read; workouts: Log_Workout[] }
  | { read: RecentHistoryRead.Unreadable };

/** The best recent set, or the fact that the history could not be read (which is not "no history"). */
type BestRecentSetRead =
  | { read: RecentHistoryRead.Read; bestSet: SetStats | undefined }
  | { read: RecentHistoryRead.Unreadable };

/** The sub-slot one workout entry is drawn with, and where it came from. */
interface ResolvedWorkoutExerciseSubSlot {
  /** a copy the caller owns */
  planSubSlot: Plan_SubSlot;
  /** the program holds no entry under this id any more (#4614) */
  missingFromPlan: boolean;
  /** the copy is a finished session's own record, not what the program says today (#4939) */
  isHistorical: boolean;
}

/**
 * Data for displaying training program overview
 */
export interface TrainingProgramData {
  fitnessPlan: FitnessPlan;
  declaredState: DeclaredState;
  fitnessState: FitnessState;
}

export interface ExerciseOverviewInfo {
  name: string;
  sets: number;
  useMyoReps: boolean;
  rmTesting: boolean;
}

export interface WorkoutOverviewDesc {
  letter: string;
  exercises: ExerciseOverviewInfo[];
}

export interface WorkoutsOverviewData {
  unavailable?: string;
  expanded: boolean;
  dailyAvg?: string;
  letters?: string;
  overallExercises: string;
  overallSets: string;
  allLetters: string;
  exercisesActiveInactive?: string;
  list: WorkoutOverviewDesc[];
}

export interface MuscleVolumeEntry {
  name: string;
  volume: number;
}

export interface ProgramOverviewData {
  workouts: WorkoutsOverviewData;
  lowerBodyMuscles: MuscleVolumeEntry[];
  upperBodyMuscles: MuscleVolumeEntry[];
}

@Injectable({ providedIn: 'root' })
export class WorkoutLoaderService {

  constructor(
    public exerciseDb: ExerciseDbService,
    private userDb: UserDb,

    private logging: LoggingService,
    private databaseRecovery: DatabaseRecoveryService,
    private debug: DebugService,
    private settings: SettingsService,
  ) { }

  // get the user's preferred weight unit from settings (set by cs-input-measure when they enter a value)
  // prefer the explicit lifting pref; fall back to the bodyweight pref as same-family evidence
  // (if the user tracks body weight in kg/lb, they most likely want the same family for lifting)
  private getDefaultWeightUnit(): string | undefined {
    return this.settings.getUnitPref('addedweight_kg') || this.settings.getUnitPref('bodyweight_kg');
  }

  // initialize weight_unit on a new exercise stat: prefer user's settings, fall back to locale
  private initWeightUnitIfMissing(stat: ExerciseStat, locale: LocaleSettings) {
    if (!stat.weight_unit) {
      stat.weight_unit = getUnitWithLocaleFallback(undefined, locale, this.getDefaultWeightUnit());
    }
  }

  // ============================================================
  // Stages shared by the per-exercise load paths
  // ============================================================
  // Loading an exercise for display, switching one permanently, swapping one in
  // for this session only, and rebuilding an exercise's sets all need the same
  // few steps. They are written once here so a change to a stage reaches every
  // path, instead of having to be copied by hand into each one.
  //
  // Where a path deliberately does something the others do not, that difference
  // stays at its own call site with a comment saying why — folding a difference
  // in here would change behaviour, which these helpers must never do.

  // get this exercise's stat record, creating an empty one when it has none,
  // and give the record a weight unit if it does not have one yet
  private getOrCreateExerciseStat(
    fitnessState: FitnessState,
    exerciseId: string,
    locale: LocaleSettings,
  ): ExerciseStat {
    let stat = fitnessState.exerciseStats[exerciseId];
    if (!stat) {
      stat = {} as ExerciseStat;
      fitnessState.exerciseStats[exerciseId] = stat;
    }
    this.initWeightUnitIfMissing(stat, locale);
    return stat;
  }

  // copy the weight unit the equipment config uses onto the exercise stat, so
  // the two cannot disagree about what a displayed weight means. public because
  // workout-detail's rebuildSets applies the same rule, and the rule lives here
  syncStatUnitFromEquipment(uie: UiExercise, stat: ExerciseStat): void {
    if (stat && uie.equipment && uie.equipment.weight_unit && uie.equipment.weight_unit !== stat.weight_unit) {
      stat.weight_unit = uie.equipment.weight_unit;
    }
  }

  // build this exercise's sets and point each one back at the exercise it
  // belongs to, so a set can reach its exercise from the template
  private buildAndAssignSets(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    mode: WorkoutLoadMode,
  ): void {
    const sets = this.buildSets(uie, uie.logExercise, uie.planSubSlot, declaredState, fitnessState, mode);
    for (const s of sets) {
      s.uiExercise = uie;
    }
    uie.sets = sets;

    // remember the myo-reps setting these sets were built with, so workout-detail can
    // tell when the setting has really changed and the sets need rebuilding.
    // recorded on EVERY build, the fresh load included: when only the rebuild recorded
    // it, a freshly loaded exercise had no value, the page's check read that as "the
    // setting changed", and it rebuilt the exercise on first entry for no reason —
    // closing the set the page had just opened (df-7499557dbb0a).
    uie.builtWithMyoReps = this.willUseMyoReps(uie, declaredState, fitnessState);
  }

  // work out this exercise's badges and its header line, and assign both.
  // returns the description result so each caller can use the parts it needs:
  // only the full load acts on `needsExerciseSwitch` and `updatedTargetWeight_kg`,
  // and the other callers discard them.
  //
  // badges are computed before the description because nothing the description
  // writes (`hasPeriodizationDescription`, `planPath.reachedMaxWeightConfiguration`)
  // is read when building badges, so the order is free and one order is simpler
  // to reason about than three.
  private applyBadgesAndDescription(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    mode: WorkoutLoadMode,
    locale: LocaleSettings,
  ): { description: string; needsExerciseSwitch: boolean; updatedTargetWeight_kg?: number } {
    uie.badges = this.getExerciseBadges(uie, declaredState, fitnessState);
    const descriptionResult = this.buildExerciseDescription(uie, mode, locale);
    uie.description = descriptionResult.description;
    return descriptionResult;
  }

  // take this exercise off the progression the plan had lined up for it. Used by
  // the two temp-swap paths, which run the session's exercise off-plan.
  // `performExerciseSwitchAsync` clears a smaller set on purpose — see the
  // comment at its own clear.
  private clearTempSwapProgression(uie: UiExercise): void {
    uie.planPath = undefined;
    uie.targetWeight_kg = undefined;
    delete uie.reducedTargetWeight_pc;
    delete uie.reducedTargetReps_delta;
    delete uie.layoffWeight_kg;
    delete uie.layoffCut1RM_net_kg;
    // #5176: the cap's own guard already refuses a temp-swapped exercise on the load path, so
    // nothing capped this one just now. What this clears is a cap the exercise picked up EARLIER in
    // the session, while it was still on-plan and before the swap took it off — that one would
    // otherwise outlive the prescription it belonged to.
    delete uie.prescriptionCap;
    // #4822: the note about last session's deload belongs to the plan's progression, which a temp
    // swap has just taken this exercise off
    delete uie.deloadCarriedOver;
  }

  /**
   * Get the effective plan reference for a workout.
   * If the workout has a stored planReference (was saved to DB), returns it.
   * Otherwise, calculates it dynamically by finding the last completed/in-progress
   * workout before this one and returning the next in cycle.
   * @param ignoreSaved If true, always compute dynamically (used to get "original" planReference)
   */
  public getEffectivePlanReference(logWorkout: Log_Workout, plan: FitnessPlan, ignoreSaved = false): number {
    return this.getPlanReferenceAnswer(logWorkout, plan, ignoreSaved).planReference;
  }

  /**
   * The same answer, plus where it came from (#5640).
   *
   * Callers that only DISPLAY a workout want `getEffectivePlanReference` and its plain
   * number. A caller about to WRITE the answer into the document must go through
   * `resolvePlanReferenceForStampingAsync` instead, because the look-back below reads an
   * in-memory cache that any screen may have emptied, and an emptied cache looks exactly
   * like a user who has never trained.
   */
  public getPlanReferenceAnswer(
    logWorkout: Log_Workout,
    plan: FitnessPlan,
    ignoreSaved = false,
    noSavedWorkoutBeforeDay?: string,
  ): PlanReferenceAnswer {
    // if already defined (saved workout) and not ignoring, return it
    if (!ignoreSaved && logWorkout.planReference !== undefined) {
      return { planReference: logWorkout.planReference, source: PlanReferenceSource.StoredOnDocument };
    }

    // find the previous workout, and note how far back the cache could actually see
    const { previous, searchedFromDay } =
      this.userDb.backlogCache.findPreviousWorkoutInCacheWithCoverage(logWorkout);
    const nowLocal = this.debug.getNowLocal();

    let previousFacts: PreviousWorkoutFacts | null = null;
    if (previous) {
      previousFacts = {
        planReference: this.getEffectivePlanReference(previous, plan),
        state: previous.state,
        dateISO: getEntryLocalTimeInDesiredTimeZone(previous, nowLocal).toISODate(),
      };
    }

    return decideNextPlanReference(
      previousFacts,
      previous
        ? LookBackCoverage.FoundPrevious
        : describeEmptyLookBack(searchedFromDay, this.earliestLogDay, this.earliestLogDayLookedUp, noSavedWorkoutBeforeDay),
      plan.workouts.length,
      nowLocal.toISODate(),
    );
  }

  /**
   * Whether anything has yet looked up where this user's log starts. Kept apart from the day
   * itself on purpose: "the user has nothing logged at all" proves a workout is their first,
   * while "nobody has looked" proves nothing, and one field cannot say both (#5640).
   *
   * Only `resolvePlanReferenceForStampingAsync` sets these, and only when it needs them, so
   * the display path never pays for the read. Held for the session because the day a user's
   * log starts does not move.
   */
  private earliestLogDayLookedUp = false;

  /** The day this user's log starts, or undefined when they have no log entries at all. */
  private earliestLogDay: string | undefined = undefined;

  /**
   * The plan reference to WRITE into a workout document, or undefined when the app cannot
   * work it out well enough to store it (#5640).
   *
   * Unlike the synchronous accessors, this one may go and fetch what it needs: it widens the
   * cached window until the previous workout comes into view, and reads the user's first log
   * date so that "this really is their first workout" can be told apart from "the cache was
   * empty". When it still cannot tell, it reports and answers undefined — leaving the
   * document unstamped, which displays exactly as an unstamped document always has.
   */
  public async resolvePlanReferenceForStampingAsync(logWorkout: Log_Workout, plan: FitnessPlan): Promise<number | undefined> {
    // a plan with no workouts has nothing to point at, and no amount of looking will change
    // that — do not pay for the reads below, and do not report a condition nobody can act on
    if (!plan.workouts?.length) {
      return undefined;
    }

    const alreadyKnown = this.storablePlanReference(logWorkout, plan);
    if (alreadyKnown !== undefined) {
      return alreadyKnown;
    }

    // Nothing was found in the window the cache happens to hold. Widen it and look again.
    // Opening a workout by id fills only 7 days either side, so someone who last trained 8
    // days ago has never had their previous workout in view at all. Most users are answered
    // by the first window; the later ones only run for someone who trains rarely. Each one is
    // a real cost — the read re-runs the schedule build over the whole widened span — which is
    // why they widen in steps instead of going straight back six months.
    const workoutTimeLocal = getEntryLocalTimeInDesiredTimeZone(logWorkout, this.debug.getNowLocal());
    for (const daysBack of [14, 45, 180]) {
      try {
        await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.getLogEntriesInRangeAsync(
          workoutTimeLocal.minus({ days: daysBack }).startOf('day'),
          workoutTimeLocal.endOf('day'),
          watcher,
        ));
      } catch (err) {
        // a read that could not run tells us nothing, so stay unstamped rather than guess
        this.logging.trackSilentProblem('workout: could not widen the look-back for a plan reference', {
          docId: logWorkout._id,
          daysBack,
          error: describeErrorForLog(err),
        });
        return undefined;
      }
      const afterWidening = this.storablePlanReference(logWorkout, plan);
      if (afterWidening !== undefined) {
        return afterWidening;
      }
    }

    // Still nothing. Either this really is the user's first workout, or the look-back cannot
    // see far enough. The day their log starts is what decides between the two — but only once
    // this device holds the older history. Before that, nothing is looked up or remembered, so
    // the look-back stays unproven and a later save looks again (df-bf4f51790720).
    const backgroundSyncComplete = this.userDb.database.isBackgroundSyncComplete();
    if (!this.earliestLogDayLookedUp && mayTrustEarliestLogDay(backgroundSyncComplete)) {
      try {
        const earliest = await this.userDb.getEarliestLogDateAsync();
        this.earliestLogDay = earliest ? earliest.toISODate() : undefined;
        this.earliestLogDayLookedUp = true;
      } catch (err) {
        this.logging.trackSilentProblem('workout: could not read the earliest log date for a plan reference', {
          docId: logWorkout._id,
          error: describeErrorForLog(err),
        });
        return undefined;
      }
    }

    const afterLogDateRead = this.storablePlanReference(logWorkout, plan);
    if (afterLogDateRead !== undefined) {
      return afterLogDateRead;
    }

    // Still nothing, and the log starts further back than the look-back reaches: someone who
    // logged meals for months before their first workout (df-afa259234d1a). If no workout was
    // ever saved before the searched run, there is nothing back there to find.
    const earlierWorkouts = await this.checkForSavedWorkoutBeforeSearchedRunAsync(logWorkout);
    if (earlierWorkouts.outcome === EarlierWorkoutCheck.ReadFailed) {
      // already reported; a read that could not run tells us nothing, so stay unstamped
      return undefined;
    }
    if (earlierWorkouts.outcome === EarlierWorkoutCheck.NoneBefore) {
      const afterWorkoutRead = this.storablePlanReference(logWorkout, plan, earlierWorkouts.noSavedWorkoutBeforeDay);
      if (afterWorkoutRead !== undefined) {
        return afterWorkoutRead;
      }
    }

    // Report once per document per session. A workout that cannot be placed stays that way,
    // and every later save would otherwise file the same problem again — which buries it
    // rather than surfacing it.
    if (!this.reportedUnknownPlanReferenceDocIds.has(logWorkout._id)) {
      this.reportedUnknownPlanReferenceDocIds.add(logWorkout._id);
      this.logging.trackSilentProblem('workout: plan reference unknown, left unstamped', {
        docId: logWorkout._id,
        workoutCount: plan.workouts.length,
        earliestCachedDay: this.userDb.backlogCache.getEarliestCachedDay(),
        earliestLogDay: this.earliestLogDay,
        backgroundSyncComplete,
        cachedDayCount: this.userDb.backlogCache.getCachedDays().length,
      });
    }
    return undefined;
  }

  /**
   * Ask the question again and hand back the number only if the answer may be stored.
   * Undefined means "still not settled" — the caller then tries something that might change
   * the answer, or gives up.
   */
  private storablePlanReference(logWorkout: Log_Workout, plan: FitnessPlan, noSavedWorkoutBeforeDay?: string): number | undefined {
    const answer = this.getPlanReferenceAnswer(logWorkout, plan, false, noSavedWorkoutBeforeDay);
    return mayStampPlanReference(answer) ? answer.planReference : undefined;
  }

  /**
   * Whether any workout was saved before the run of days the cache searched, read from the
   * log's ids (df-afa259234d1a). On `NoneBefore` it also names the local day the proof reaches.
   * A failed read is reported here and answers `ReadFailed`.
   *
   * It covers only the days BEFORE the searched run, never the run itself: the walk over the
   * cache also sees scheduled workouts that were never saved, and a read of saved ids cannot.
   * Before that run, an unsaved scheduled workout cannot change the answer: a past one that was
   * never started does not move the rotation on, and a skipped one is saved.
   */
  private async checkForSavedWorkoutBeforeSearchedRunAsync(
    logWorkout: Log_Workout,
  ): Promise<EarlierWorkoutCheckResult> {
    const { previous, searchedFromDay } = this.userDb.backlogCache.findPreviousWorkoutInCacheWithCoverage(logWorkout);
    if (previous || searchedFromDay === undefined || this.earliestLogDay === undefined) {
      return { outcome: EarlierWorkoutCheck.NotRun };
    }
    // where the read must stop, or nothing when this device cannot prove anything with it yet
    const readUpToUtcDay = earlierWorkoutReadEndUtcDay(searchedFromDay, this.userDb.database.isBackgroundSyncComplete());
    if (readUpToUtcDay === undefined) {
      return { outcome: EarlierWorkoutCheck.NotRun };
    }
    try {
      const found = await this.userDb.hasSavedWorkoutBeforeUtcDayAsync(readUpToUtcDay);
      return found
        ? { outcome: EarlierWorkoutCheck.FoundOne }
        : { outcome: EarlierWorkoutCheck.NoneBefore, noSavedWorkoutBeforeDay: searchedFromDay };
    } catch (err) {
      this.logging.trackSilentProblem('workout: could not check for earlier workouts for a plan reference', {
        docId: logWorkout._id,
        error: describeErrorForLog(err),
      });
      return { outcome: EarlierWorkoutCheck.ReadFailed };
    }
  }

  /**
   * Documents already reported as unplaceable this session, so one stuck workout files one
   * Sentry problem rather than one per save.
   */
  private readonly reportedUnknownPlanReferenceDocIds = new Set<string>();

  /**
   * Get the effective exercises for a workout.
   * If the workout has no stored exercises, creates exercise stubs from the plan based on the
   * effective planReference. Otherwise returns the stored exercises — merged with the current
   * plan only when `planMerge` asks for it and the workout is not finished or skipped.
   *
   * `planMerge` is required so every caller decides (#6574). Pass `planMergeForMode(mode)` for
   * the workout a page is showing, and `PlanMerge.AsStored` for anything that reads logged sets:
   * an unfinished workout from an earlier day must not take on exercises from a plan the user
   * changed after abandoning it. See design-docs/workout-plan-reference.md.
   *
   * It never assigns the list to the workout (#6585). A caller that wants the merged list saved,
   * such as `loadWorkoutExercisesAsync`, assigns it with `setWorkoutExercises`.
   */
  public getEffectiveExercises(logWorkout: Log_Workout, plan: FitnessPlan, planMerge: PlanMerge): Log_WorkoutExercise[] {
    // for each stored exercise, report and heal a sparse `sets` array (issue #720)
    // before anything reads it. Runs ahead of EVERY branch below on purpose: the
    // early returns hand the stored exercises straight back, and their callers
    // iterate `sets` themselves. Also the reason this path reports at all — the
    // merge below used to throw on a hole before any caller's own validity check
    // could send the report, so the #720 signal undercounted the worst-affected
    // accounts. The same loop recalculates the strength figures of a set whose weight was
    // rewritten before bf49fb31e and left the old figures behind (#5175).
    for (const exercise of logWorkout.exercises ?? []) {
      if (exercise) {
        this.logging.checkWorkoutExerciseValidity('getEffectiveExercises', exercise);
        this.repairStaleSetFiguresOnRead(logWorkout, exercise);
      }
    }

    // the #5640 plan-reference repair deliberately does NOT run here (#6565). This method reads
    // past workouts for every history screen, and a past session may have been trained under a
    // plan the user has since changed. It runs in `loadWorkoutExercisesAsync` instead, for the
    // workout the user can train now.

    // get effective plan reference
    const planRef = this.getEffectivePlanReference(logWorkout, plan);
    const planWorkout = plan.workouts[planRef];

    if (!planWorkout) {
      return logWorkout.exercises?.length ? logWorkout.exercises : [];
    }

    // if no saved exercises, create stubs from plan
    if (!logWorkout.exercises || logWorkout.exercises.length === 0) {
      const exercises: Log_WorkoutExercise[] = [];
      for (const subSlotId of planWorkout.subSlotIds) {
        exercises.push({ subSlotId, sets: [] });
      }
      return exercises;
    }

    // skip merge for completed workouts — their exercises are historical
    if (logWorkout.state === LogRecordState.Done || logWorkout.state === LogRecordState.Skipped) {
      return logWorkout.exercises;
    }

    // and wherever the caller asked for the stored list (#6574). The merge below drops a hole
    // in the list (a null entry an older version wrote, df-1a9b71682623), and these callers used
    // to get the merged list, so drop holes here too or they would crash on them. Hand back a
    // new list only when there is a hole, so callers that write through the stored list keep it.
    if (planMerge === PlanMerge.AsStored) {
      return storedExercisesWithoutHoles(logWorkout.exercises);
    }

    // merge existing exercises with current plan. hand the merged list back without writing it
    // into the workout (#6585): a database read returns the one shared in-memory copy of the
    // document, so a screen that only shows this workout would otherwise rewrite it for every
    // other screen. a caller that means to change the workout assigns the list itself.
    const { mergedExercises, changed } = mergeWorkoutExercisesWithPlan(
      logWorkout.exercises,
      planWorkout.subSlotIds,
      plan.subSlots,
    );

    return changed ? mergedExercises : logWorkout.exercises;
  }

  /**
   * #5175: a logged set whose weight was rewritten before bf49fb31e can still carry the strength
   * figures of the old weight. Recalculate them here, where every screen reads a workout's sets,
   * so the progress chart, the CSV export and the "previous session" figure stop showing a
   * one-rep max the user never lifted. Only sets the repair can make exact are touched; see
   * `repairStaleSetFigures`.
   *
   * The repair is made to the copy in memory. It reaches the database only if this workout is
   * saved afterwards, which for a finished workout rarely happens, so a screen that reads a fresh
   * copy repairs the same sets again. That is harmless — the answer is the same every time — but
   * it would report every read, so each workout and exercise is reported once per app session.
   * Reported as info because the repair has already run.
   */
  private repairStaleSetFiguresOnRead(logWorkout: Log_Workout, exercise: Log_WorkoutExercise): void {
    const repaired = repairStaleSetFigures(exercise.sets);
    const reportKey = `${logWorkout._id}|${exercise.exerciseId}`;
    if (repaired.length > 0 && !this.reportedStaleSetFigureKeys.has(reportKey)) {
      this.reportedStaleSetFigureKeys.add(reportKey);
      this.logging.trackSilentInfo('Issue #5175 - repaired stale set figures @getEffectiveExercises', {
        workoutId: logWorkout._id,
        exerciseId: exercise.exerciseId,
        repaired,
      });
    }
  }

  /** Workout and exercise pairs whose stale set figures were already reported this session (#5175). */
  private readonly reportedStaleSetFigureKeys = new Set<string>();

  /**
   * Put right a workout whose stored `planReference` its own logged sets contradict (#5640).
   *
   * The reporter's Wednesday workout was filled with Workout B's exercises, he logged ten
   * sets on them, and then a look-back over an emptied cache wrote "Workout A" and
   * `planReference: 0` over the top. From then on his day showed Workout A's exercises and
   * his session was out of reach.
   *
   * The repair only fires where the sets themselves settle the question: every exercise he
   * actually logged a set on belongs to exactly one OTHER workout of the plan, and none of
   * them belongs to the one the document claims. A user who deliberately changed workout
   * has an exercise list that matches their label, so this never touches them; nor does a
   * plan edit that moved a single exercise between days, because the other logged exercises
   * would still match.
   *
   * It runs only for the workout the user can train now (`mayHealPlanReferenceInMode`, #6565),
   * the one workout sure to have been trained under the plan it is judged against. The change
   * stays in memory until the workout is saved, so a fresh copy is repaired again on every load;
   * the report goes out once per document per app session.
   */
  private healPlanReferenceContradictedByLoggedSets(logWorkout: Log_Workout, plan: FitnessPlan): void {
    const storedRef = logWorkout.planReference;
    if (storedRef === undefined) return;
    // a finished session is history — its exercises are not re-derived from the plan, so
    // there is nothing here to protect it from
    if (logWorkout.state === LogRecordState.Done || logWorkout.state === LogRecordState.Skipped) return;
    if (!plan.workouts?.length) return;

    // the sub-slots the user has actually logged a set on
    const workedSubSlotIds = subSlotIdsWithAFinishedSet(logWorkout.exercises);
    if (workedSubSlotIds.length === 0) return;

    const correctedRef = decidePlanReferenceHeal(
      storedRef,
      workedSubSlotIds,
      plan.workouts.map(w => w?.subSlotIds ?? []),
    );
    if (correctedRef === undefined) return;
    this.setWorkoutPlanReference(logWorkout, correctedRef);
    const correctedName = plan.workouts[correctedRef]?.name;
    // clear the old name when the corrected workout has none of its own, rather than leaving
    // "Workout A" sitting beside a reference that now says otherwise — two fields disagreeing
    // is the state this whole repair exists to end. An empty name is derived from the
    // reference by `getEffectiveWorkoutName`, so nothing is lost on screen.
    this.setWorkoutName(logWorkout, correctedName ? 'Workout ' + correctedName : '');

    // reported at problem tier, not info: the repair did run, but we still want to count how
    // many accounts the original write reached
    if (!this.reportedPlanReferenceHealDocIds.has(logWorkout._id)) {
      this.reportedPlanReferenceHealDocIds.add(logWorkout._id);
      this.logging.trackSilentProblem('workout: plan reference healed from logged sets', {
        docId: logWorkout._id,
        storedPlanReference: storedRef,
        correctedPlanReference: correctedRef,
        workedSubSlotCount: workedSubSlotIds.length,
      });
    }
  }

  /** Workouts whose plan-reference repair was already reported this session (#6565). */
  private readonly reportedPlanReferenceHealDocIds = new Set<string>();

  /**
   * Forget a stored plan reference that points at a workout the plan no longer has, when the
   * workout holds nothing the user trained (#6850). Edit Workouts can delete the day a workout
   * was stamped with, and it never touches workouts already in the log. Clearing all three
   * stamped fields lets `loadWorkoutExercisesAsync` decide them again together, as for any
   * unstamped workout. The change stays in memory until the workout is saved.
   */
  private resetOutOfRangePlanReference(logWorkout: Log_Workout, plan: FitnessPlan): void {
    const storedRef = logWorkout.planReference;
    const workoutCount = plan.workouts?.length ?? 0;
    const repair = decideOutOfRangePlanReferenceRepair(storedRef, workoutCount, logWorkout.state, logWorkout.exercises);
    if (repair === OutOfRangePlanReferenceRepair.InRange) return;

    const firstReport = !this.reportedOutOfRangePlanReferenceDocIds.has(logWorkout._id);
    this.reportedOutOfRangePlanReferenceDocIds.add(logWorkout._id);
    const context = { docId: logWorkout._id, storedPlanReference: storedRef, workoutCount };

    // if the user trained in it, its sets are what they did; the merge keeps them on screen
    if (repair === OutOfRangePlanReferenceRepair.LeaveStarted) {
      if (firstReport) this.logging.trackSilentProblem('workout: out-of-range plan reference on a started workout', context);
      return;
    }

    delete logWorkout.planReference;
    delete logWorkout.name;
    logWorkout.exercises = [];
    if (firstReport) this.logging.trackSilentInfo('workout: out-of-range plan reference reset', context);
  }

  /** Workouts whose out-of-range plan reference was already reported this session (#6850). */
  private readonly reportedOutOfRangePlanReferenceDocIds = new Set<string>();

  /**
   * Get the effective workout name in its CANONICAL ENGLISH form ("Workout A").
   *
   * If the workout has a stored name (was saved to DB), returns it verbatim.
   * Otherwise, derives the name from the plan based on effective planReference.
   *
   * This is deliberately NOT translated (#4317). The value is used for two
   * different jobs and only one of them may be localized:
   *  - persistence (`setWorkoutName`) — must stay English, so a user's language
   *    is never frozen into their data and the name stays comparable across
   *    languages;
   *  - display — the caller wraps the result in `formatWorkoutName()` from
   *    util/formatting.
   *
   * Returning a translated string here would silently leak the display form
   * into storage via `saveWorkoutAsync`, which is exactly the bug this comment
   * exists to prevent. Translate at the display site, never here.
   */
  public getEffectiveWorkoutName(logWorkout: Log_Workout, plan: FitnessPlan): string {
    // if already defined (saved workout), return the stored English name as-is
    if (logWorkout.name) {
      return logWorkout.name;
    }

    // derive from plan
    const planRef = this.getEffectivePlanReference(logWorkout, plan);
    const planWorkout = plan.workouts[planRef];

    // return the canonical "Workout {letter}" form; the letter comes from the plan
    if (planWorkout?.name) {
      return 'Workout ' + planWorkout.name;
    }
    return '';
  }

  /**
   * Set workout name. Only use when saving to DB or changing workout.
   */
  public setWorkoutName(logWorkout: Log_Workout, name: string): void {
    logWorkout.name = name;
  }

  /**
   * Set workout plan reference. Only use when saving to DB or changing workout.
   */
  public setWorkoutPlanReference(logWorkout: Log_Workout, planReference: number): void {
    logWorkout.planReference = planReference;
  }

  /**
   * Set workout exercises. Only use when saving to DB or changing workout.
   */
  public setWorkoutExercises(logWorkout: Log_Workout, exercises: Log_WorkoutExercise[]): void {
    logWorkout.exercises = exercises;
  }

  /**
   * Load exercise data and mutate UiExercise in place.
   * Also links sets back to the UiExercise instance.
   * Caller must set uie.logExercise and uie.planSubSlot before calling.
   */
  public async loadExerciseAsync(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): Promise<LoadExerciseResult> {
    const { plan, fitnessState, declaredState, nowLocal } = context;

    // get the slot, and skip avoided exercises
    const slot = await this.exerciseDb.slotForIdAsync(uie.planSubSlot.slotId, context.watcher);
    // #4939: not on a FINISHED workout. This rotates the sub-slot onto a different exercise when
    // the bound one has been deleted or is being avoided, which is a decision about what the
    // user will perform NEXT. Applied to a finished session it rewrites which exercise the
    // history shows, and `replaceExerciseInSubSlot` also clears that copy's progression state.
    // Nothing is lost by skipping it: the exercise-resolution stage below prefers the exercise
    // the log itself names, and `getExerciseForSubSlot` already substitutes an alternative when
    // that id cannot be looked up at all.
    if (mode !== WorkoutLoadMode.Past) {
      await this.skipAvoidedExercises(slot, uie.planSubSlot, fitnessState, plan, nowLocal, context.watcher);
    }

    // resolve which exercise this sub-slot is showing
    const exerciseId = uie.logExercise.exerciseId || uie.planSubSlot.exercise_id;

    // if neither source provided an exercise id, log context for debugging (#2634)
    if (!exerciseId) {
      this.logging.trackSilentProblem('loadExerciseAsync: exerciseId is falsy', {
        subSlotId: uie.logExercise.subSlotId,
        logExerciseId: uie.logExercise.exerciseId,
        planSubSlotExerciseId: uie.planSubSlot.exercise_id,
        planSubSlotSlotId: uie.planSubSlot.slotId,
        setsCount: uie.logExercise.sets?.length,
        doneSetsCount: uie.logExercise.sets?.filter(s => s?.state === LogRecordState.Done).length,
      });
    }

    // a curated read that failed travels, and the per-exercise catch above skips this exercise
    // for this load: a stand-in here would become the exercise the user performs (df-c21e11979ef2)
    const exercise = await getExerciseForSubSlot(
      uie.planSubSlot,
      exerciseId,
      this.exerciseDb,
      this.userDb,
      fitnessState,
      plan,
      nowLocal.toUTC(),
      context.watcher,
    );

    if (!exercise) {
      throw new Error(`Cannot find exercise for subSlot ${uie.logExercise.subSlotId}`);
    }

    // put the resolved exercise and slot on the UiExercise
    uie.exercise = exercise;
    uie.slot = slot;

    // get or create the exercise stat, and give it a weight unit
    const stat = this.getOrCreateExerciseStat(fitnessState, exercise._id, context.locale);
    uie.stat = stat;

    // record whether this session is in the future
    uie.startedInFuture = mode < WorkoutLoadMode.Current;

    // start from an empty set list, which the helper methods below read
    uie.sets = [];

    // rebuild paths if needed (always call this, similar to openExercise in workout-detail)
    this.rebuildPathsIfNeeded(uie, declaredState, fitnessState);

    // pick the path this session runs on
    this.setCurrentPath(uie);

    // Equipment must be initialized before the layoff step below, which rounds the layoff weight
    // onto it and checks whether a cut would show on it at all. checkEquipment is idempotent and
    // the later steps are unaffected by its earlier invocation.
    uie.checkEquipment(fitnessState, this.userDb, context.locale, this.logging);

    // the weight this session starts from; the layoff step and the #5176 cap below may lower it
    uie.targetWeight_kg = uie.planPath?.target_weight_kg;

    // a break long enough to force a strength retest rebuilds the paths, so it is decided HERE,
    // before the path repair. How much a shorter break takes off is decided after the repair, by
    // `applyLayoffPrescriptionAsync`, because it reads the same recent history as the cap.
    //
    // #4939: the layoff is skipped on a FINISHED workout. It asks how long it has been since the
    // exercise was last trained and cuts the target when the answer is long enough, which is a
    // statement about the session the user is ABOUT to perform. On a session performed months ago
    // the answer is always "a long time", so every such session was drawn with a reduced target and
    // any older than three weeks was redrawn as a strength test — neither of which is what the user
    // actually did. `onClickSetDone` still has the `targetWeight_kg` set just above.
    const daysSinceLastTrained = this.daysSinceLastTrainedForLayoff(uie, mode, nowLocal);
    if (daysSinceLastTrained !== undefined && reducedTargetWeightAfterDays(daysSinceLastTrained) === 'retest') {
      uie.reducedTargetWeight_pc = -1;
      // rebuild the paths so the path type changes to RMTest/NonRMTest
      this.rebuildPathsIfNeeded(uie, declaredState, fitnessState);
      this.setCurrentPath(uie);
    }

    // repair an invalid path state
    const pathFixupApplied = this.fixupInvalidPathState(uie, declaredState, fitnessState);

    // #5617: the layoff and the #5176 cap both judge the user by their best recent set. Read it at
    // most once, and only when one of them actually asks.
    // #4822: the deload note below reads the same recent workouts, so the read itself is shared too
    let recentWorkouts: Promise<RecentWorkoutsRead> | undefined;
    const readRecentWorkoutsAsync = (): Promise<RecentWorkoutsRead> => {
      recentWorkouts ??= this.readRecentWorkoutsAsync(uie);
      return recentWorkouts;
    };
    let recentHistory: Promise<BestRecentSetRead> | undefined;
    const readRecentHistoryAsync = (): Promise<BestRecentSetRead> => {
      recentHistory ??= this.findBestRecentSetAsync(uie, context, readRecentWorkoutsAsync);
      return recentHistory;
    };

    // the weight after a shorter break (#5617). Runs before the cap, which then holds the layoff
    // weight down like any other planned weight.
    await this.applyLayoffPrescriptionAsync(uie, context, daysSinceLastTrained, readRecentHistoryAsync);

    // #5176: hold the prescribed weight down to something the user can actually perform for the
    // prescribed reps. Runs HERE — after every step above has settled `targetWeight_kg` and after
    // the path repair, but before `buildAndAssignSets` prefills the sets from it and before
    // `applyBadgesAndDescription` renders it into the header. Applied any later and the header
    // would name one weight while the set row offered another.
    await this.applyPrescribedWeightCapAsync(uie, context, mode, readRecentHistoryAsync);

    // #4822: say why the weight is one step lower when last session's deload lowered it
    await this.applyDeloadCarriedOverAsync(uie, context, mode, readRecentWorkoutsAsync);

    // synchronize units from the equipment config
    this.syncStatUnitFromEquipment(uie, stat);

    // heal sparse `sets` arrays (issue #720) before buildSets sees them.
    // writes Skipped placeholders into null slots that precede a terminal set
    // so the canonical doc no longer carries nulls.
    const healedIndices = healSparseLogSets(uie.logExercise.sets);
    const sparseSetsHealed = healedIndices.length > 0;
    if (sparseSetsHealed) {
      this.logging.trackSilentInfo('Issue #720 - healed @loadExerciseAsync', {
        subSlotId: uie.logExercise.subSlotId,
        exerciseId: uie.logExercise.exerciseId,
        healedIndices,
        setsCount: uie.logExercise.sets.length,
      });
    }

    // build the sets and link them back to uie
    this.buildAndAssignSets(uie, declaredState, fitnessState, mode);

    // work out the badges and the header line
    const descriptionResult = this.applyBadgesAndDescription(uie, declaredState, fitnessState, mode, context.locale);
    const needsExerciseSwitch = descriptionResult.needsExerciseSwitch;

    // if target weight was updated during description building
    if (descriptionResult.updatedTargetWeight_kg !== undefined) {
      uie.targetWeight_kg = descriptionResult.updatedTargetWeight_kg;
    }

    // carry over whether the user has seen this exercise's tutorial
    uie.tutorialSeen = stat.tutorialSeen || false;

    return { needsExerciseSwitch, pathFixupApplied, sparseSetsHealed };
  }

  /** Days since this exercise was last trained, when a training break can change this session at
   *  all; undefined when it cannot: a finished workout (#4939), a path with no successful set yet,
   *  a path that is already a strength test, or an exercise never concluded before. */
  private daysSinceLastTrainedForLayoff(uie: UiExercise, mode: WorkoutLoadMode, nowLocal: DateTime): number | undefined {
    const path = uie.planPath;
    if (!path || mode === WorkoutLoadMode.Past || !path.lastStats) return undefined;
    if (path.nextWorkoutSetType === SetType.RMTest || path.nextWorkoutSetType === SetType.NonRMTest) return undefined;
    const lastWorkoutConcluded_utc = uie.planSubSlot.lastTimeConcluded_utc;
    if (!lastWorkoutConcluded_utc) return undefined;
    return daysSinceDate(nowLocal, lastWorkoutConcluded_utc);
  }

  /**
   * Lower this session's prescription after a 9-22 day break (#5617).
   *
   * The cut comes off the one-rep max of the user's best recent set, is run back to the path's rep
   * target, is never above the path's stored target, and takes reps off instead when it would not
   * show on the equipment. The pure rule is `decideLayoffPrescription`; the rules and the reported
   * case are in `design-docs/reactive-deload.md`, "Layoff prescription from the best recent one-rep
   * max (#5617)". Writes nothing to the plan: the first set's conclusion stores `layoffWeight_kg`.
   */
  private async applyLayoffPrescriptionAsync(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    daysSinceLastTrained: number | undefined,
    readRecentHistoryAsync: () => Promise<BestRecentSetRead>,
  ): Promise<void> {
    // clear what an earlier load of this same UiExercise left before any early return below
    delete uie.layoffWeight_kg;
    delete uie.layoffCut1RM_net_kg;

    const path = uie.planPath;
    // if there was no break, or it forced a retest, there is nothing to cut
    if (daysSinceLastTrained === undefined || uie.reducedTargetWeight_pc === -1 || !path?.lastStats) return;
    // a temp swap never touches plan progression, and `clearTempSwapProgression` wipes whatever this
    // would decide. Test the LOG's flag, as the #5176 cap does: `uie.isTempSwap` is only set after the load.
    if (uie.isTempSwap || uie.logExercise?.isTempSwap) return;
    // the path repair can turn the path into a strength test, which the layoff never touches
    if (path.nextWorkoutSetType === SetType.RMTest || path.nextWorkoutSetType === SetType.NonRMTest) return;
    if (typeof reducedTargetWeightAfterDays(daysSinceLastTrained) !== 'number') return;

    // an unreadable history is already reported by the read; the layoff then works from `lastStats`
    const recent = await readRecentHistoryAsync();
    const storedTargetBefore_kg = path.target_weight_kg;
    const layoff = decideLayoffPrescription({
      daysSinceLastTrained,
      lastStats: path.lastStats,
      targetReps: typeof path.target_reps === 'number' ? path.target_reps : undefined,
      minReps: typeof path.min_reps === 'number' ? path.min_reps : undefined,
      intensityLevel: path.intensityLevel,
      equipment: uie.equipment,
      storedTarget_kg: storedTargetBefore_kg,
      nextWorkoutSetType: path.nextWorkoutSetType,
      bestRecentSet: recent.read === RecentHistoryRead.Read ? recent.bestSet : undefined,
      bodyWeight_kg: context.fitnessState.weight_kg,
      exerciseBw_pc: uie.exercise?.bw_pc || 0,
    });
    if (layoff.outcome === LayoffOutcome.None || layoff.outcome === LayoffOutcome.Retest) return;

    // if equipment is unexpectedly missing here, the layoff weight could not be put on an equipment
    // grid, and the rule's check for a cut hidden by rounding ran on unrounded numbers. surface it so
    // a future debugger can spot the pattern.
    if (!uie.equipment) {
      this.logging.trackSilentProblem('Issue #3339 - layoff equipment missing', {
        exerciseId: uie.exercise._id,
        intensityLevel: path.intensityLevel,
      });
    }

    if (layoff.outcome === LayoffOutcome.WeightCut) {
      uie.reducedTargetWeight_pc = layoff.reduction_pc;
      uie.targetWeight_kg = layoff.weight_kg;
    } else if (layoff.outcome === LayoffOutcome.RepCut) {
      uie.reducedTargetReps_delta = layoff.repDelta;
      path.target_reps = layoff.targetReps;
      if (layoff.minReps !== undefined) {
        path.min_reps = layoff.minReps;
      }
      uie.targetWeight_kg = layoff.weight_kg;
    } else if (layoff.weight_kg !== undefined) {
      // the rep floor swallowed the rep cut: no rep changes and nothing is announced, but the
      // weight is held where the rule put it
      uie.targetWeight_kg = layoff.weight_kg;
    }

    // under the #5617 rule the conclusion stores exactly this weight; under the older rule it keeps
    // its own writes
    if (layoff.evidence !== LayoffEvidence.None && layoff.weight_kg !== undefined) {
      uie.layoffWeight_kg = layoff.weight_kg;
      uie.layoffCut1RM_net_kg = layoff.cut1RM_net_kg;
    }

    // the layoff IS the repair, applied before this reports it, so this is the "info" tier. it
    // fires again if the workout is reloaded before the first set concludes, which is accepted.
    let evidenceSet: SetStats | undefined;
    if (layoff.evidence === LayoffEvidence.BestRecentSet && recent.read === RecentHistoryRead.Read) {
      evidenceSet = recent.bestSet;
    } else if (layoff.evidence === LayoffEvidence.LastStats) {
      evidenceSet = path.lastStats;
    }
    this.logging.trackSilentInfo('Issue #5617 - layoff prescription from best recent set', {
      exerciseId: uie.exercise?._id,
      intensityLevel: path.intensityLevel,
      daysSinceLastTrained,
      reduction_pc: reducedTargetWeightAfterDays(daysSinceLastTrained),
      evidence: layoff.evidence,
      usedLastStatsFallback: layoff.evidence === LayoffEvidence.LastStats,
      historyUnreadable: recent.read === RecentHistoryRead.Unreadable,
      evidenceWeight_kg: evidenceSet?.weight_kg,
      evidenceReps: evidenceSet?.reps,
      layoffWeight_kg: uie.targetWeight_kg,
      storedTargetBefore_kg,
      repCut: layoff.outcome === LayoffOutcome.RepCut,
      repDelta: layoff.outcome === LayoffOutcome.RepCut ? layoff.repDelta : 0,
    });
  }

  /** How far back the prescription cap will look for sessions to hand to the window.
   *
   *  Larger than `PRESCRIPTION_CAP_SESSION_WINDOW` on purpose: sessions that yield nothing usable —
   *  abandoned with only prefilled sets, dated in the future, or all deload and dropset work — do
   *  not spend a slot in the window, so the read has to be able to walk past a run of them. It is
   *  also the `limit` handed to the view, and so the number of documents this fetches: the whole
   *  point of the narrow read is that it does NOT grow with how long the user has been training.
   *
   *  Since #4822 the same read also serves the deload note, which needs only the newest few. */
  private static readonly MAX_SESSIONS_SCANNED_FOR_CAP = 12;

  /** Set types whose prescription is a deliberate probe of the user's limit, so a cap must not
   *  touch them: a strength test is SUPPOSED to reach for a weight the user has not proved yet.
   *
   *  Deliberately its OWN list, even though it holds the same three values as
   *  `SET_TYPES_WITH_NO_TARGET_PRESCRIPTION` in `@cf-shared/data-model/log-workout`. That one
   *  answers "may this path have no target weight at all?"; this one answers "may a cap lower the
   *  target weight this path does have?". The two questions are free to diverge, so do not fold
   *  them together on the strength of today's values matching. */
  private static readonly SET_TYPES_EXEMPT_FROM_PRESCRIPTION_CAP: ReadonlySet<SetType> =
    new Set([SetType.RMTest, SetType.NonRMTest, SetType.RepTest]);

  /**
   * Lower `uie.targetWeight_kg` when the plan asks for a weight the user's own recent performance
   * says they cannot lift for the prescribed number of reps (#5176).
   *
   * Records the result on `uie.prescriptionCap` and deliberately does NOT write the plan — the rule,
   * why it is not persisted, and the reported case behind it are in `design-docs/reactive-deload.md`,
   * the #5176 section.
   */
  private async applyPrescribedWeightCapAsync(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
    readRecentHistoryAsync: () => Promise<BestRecentSetRead>,
  ): Promise<void> {
    // Clear any cap left over from a previous load of this same UiExercise BEFORE any early return
    // below can skip the rest. A `UiExercise` outlives a reload, an exercise switch and a temp swap,
    // so a stale cap would make `openExercise` claim a weight was lowered when nothing lowered it,
    // and would make `concludeSet` stamp `prescribedWeightCapped` on a set that was never capped —
    // judging it against the wrong weight, which is the very bug this cap exists to fix.
    delete uie.prescriptionCap;

    const path = uie.planPath;
    // a finished session is a record of what the user DID; capping it would rewrite history, and the
    // question the cap answers ("can they perform this?") is only about a session still to come
    if (mode === WorkoutLoadMode.Past) return;
    // A temp swap never touches plan progression, so there is no prescription to protect. Test the
    // LOG's flag, not `uie.isTempSwap`: that one is only set AFTER `loadExerciseAsync` returns, so on
    // the load path it is still false here. Without this the cap would read the swapped-IN exercise's
    // history, judge it against a weight prescribed for a DIFFERENT exercise, and report a cap that
    // `clearTempSwapProgression` wipes moments later.
    if (uie.isTempSwap || uie.logExercise?.isTempSwap || !path) return;
    if (WorkoutLoaderService.SET_TYPES_EXEMPT_FROM_PRESCRIPTION_CAP.has(path.nextWorkoutSetType)) return;
    // a user who reached the heaviest thing their equipment can produce, at target reps, has already
    // proved this weight — lowering it here would fight the exercise-switch suggestion instead
    if (path.reachedMaxWeightConfiguration != null) return;
    // #6428 R4: a weight set by hand in the exercise editor is the person's decision for this one
    // session; the first set conclusion on the path clears the mark, so the cap works again after
    if (isCoachSetWeight(path)) return;

    const recent = await readRecentHistoryAsync();
    if (recent.read === RecentHistoryRead.Unreadable) return;

    const bestRecentSet = recent.bestSet;
    // capture what the cap is actually comparing against BEFORE it is overwritten below. This is not
    // always the path's climbing target: the layoff block above may already have reduced it, and
    // reporting the path's number would make a layoff-plus-cap session look like a far bigger cut
    // than the cap made.
    const weightBeforeCap_kg = uie.targetWeight_kg;
    const cap = capPrescribedWeight_kg({
      bestRecentSet,
      plannedWeight_kg: weightBeforeCap_kg,
      prescribedReps: path.target_reps,
      bodyWeight_kg: context.fitnessState.weight_kg,
      exerciseBw_pc: uie.exercise?.bw_pc || 0,
      equipment: uie.equipment,
    });
    if (!cap) return;

    uie.prescriptionCap = cap;
    uie.targetWeight_kg = cap.cappedWeight_kg;

    // the cap IS the repair, and it has already been applied by the time this runs — so this is the
    // "info" tier, not the "problem" tier (see CLAUDE.md's three severity tiers)
    this.logging.trackSilentInfo('Issue #5176 - prescribed weight capped to recent performance', {
      exerciseId: uie.exercise?._id,
      exerciseName: uie.exercise?.name,
      intensityLevel: path.intensityLevel,
      plannedWeight_kg: weightBeforeCap_kg,
      pathTargetWeight_kg: path.target_weight_kg,
      cappedWeight_kg: cap.cappedWeight_kg,
      ceiling_kg: cap.ceiling_kg,
      prescribedReps: path.target_reps,
      provenWeight_kg: bestRecentSet?.weight_kg,
      provenReps: bestRecentSet?.reps,
      // a ceiling below zero says the reps are out of reach even with no added weight — a genuinely
      // different situation from an ordinary cap, and the one worth spotting in the data
      repsImpossibleUnloaded: cap.ceiling_kg < 0,
    });
  }

  /**
   * Snap the prescription cap again, onto equipment the page attached after the load (#5176).
   *
   * The cap runs inside the load, but for an exercise whose equipment was never chosen the page only
   * creates that equipment afterwards. So the load snapped the cap onto no grid at all. Without this,
   * the set row could round the capped weight UP past the ceiling, and a cut smaller than one
   * equipment step would still be announced as a lowering.
   *
   * Rebuilds the header line when the number changes, because the load built it from the unsnapped
   * weight. The set rows need nothing: `openSet` prefills them from `targetWeight_kg` when they open.
   */
  public resnapPrescriptionCapOntoEquipment(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    mode: WorkoutLoadMode,
    locale: LocaleSettings,
  ): void {
    const cap = uie.prescriptionCap;
    // if nothing was capped, there is nothing to redo
    if (!cap) return;

    const { outcome, targetWeight_kg } = resnapPrescriptionCap(cap, uie.equipment);
    if (outcome === PrescriptionCapResnap.Unchanged) return;

    if (outcome === PrescriptionCapResnap.Dropped) {
      // drop the cap so nothing announces or records a lowering the user cannot see
      delete uie.prescriptionCap;
    } else {
      cap.cappedWeight_kg = targetWeight_kg;
    }
    uie.targetWeight_kg = targetWeight_kg;
    this.applyBadgesAndDescription(uie, declaredState, fitnessState, mode, locale);
  }

  /**
   * Build the badges and the header line again, for an exercise the page attached equipment to
   * after the load.
   *
   * The load builds the header before such an exercise has equipment, so the header's weight is
   * not rounded onto the equipment's steps. Until df-7499557dbb0a that was repaired only by
   * accident: the page wrongly rebuilt every exercise on first entry, and the rebuild redrew the
   * header. The sets need nothing here — building them does not read the equipment.
   */
  public refreshBadgesAndDescription(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    mode: WorkoutLoadMode,
    locale: LocaleSettings,
  ): void {
    this.applyBadgesAndDescription(uie, declaredState, fitnessState, mode, locale);
  }

  /** The best set the user produced across their last few sessions of this exercise.
   *
   *  Reads the newest few logged sessions, NEWEST FIRST. A NARROW read, and deliberately so — this
   *  one runs before the page draws, where the full-history read is far too expensive. The
   *  measurements, and why the last-workout card still does its own full read instead of sharing
   *  this list: `design-docs/reactive-deload.md`, the #5176 section.
   *
   *  A WINDOW rather than just the latest session: the session that triggers a reactive deload is a
   *  bad one by definition, and judging by it alone clamps the next prescription far below what the
   *  user managed the week before (the E2E loop suite catches exactly that — see
   *  `PRESCRIPTION_CAP_SESSION_WINDOW`).
   *
   *  Deliberately NOT filtered by intensity level, unlike the page's last-workout card: a one-rep
   *  max is intensity-free, and filtering would hide the freshest evidence of what the user can do
   *  behind an older session that happens to match today's tier.
   *
   *  Answers `Unreadable` when the read failed: the cap is a safety net and must never be the reason
   *  a workout fails to open, so the failure is reported and the caller leaves the prescription as
   *  the plan had it. `Read` with no set means the history holds nothing usable. */
  private async findBestRecentSetAsync(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    readRecentWorkoutsAsync: () => Promise<RecentWorkoutsRead>,
  ): Promise<BestRecentSetRead> {
    if (!uie.exercise?._id) return { read: RecentHistoryRead.Unreadable };
    const recent = await readRecentWorkoutsAsync();
    if (recent.read === RecentHistoryRead.Unreadable) return { read: RecentHistoryRead.Unreadable };
    const workouts = recent.workouts;

    const recentSessions: Log_WorkoutSet[][] = [];
    // the view emits one row per exercise ENTRY, so a workout holding this exercise in two sub-slots
    // (the duplicate-subslot shape in design-docs/volume-reoptimize.md) comes back twice. Counting
    // it twice would spend two window slots on one session and quietly narrow the window.
    const seenWorkoutIds = new Set<string>();
    // `workouts` arrives NEWEST FIRST from the narrow query, so this walks forwards. Getting the
    // direction wrong would silently pick the user's OLDEST sessions and nothing would look broken.
    for (const workout of workouts) {
      // never read the session the user is in the middle of — a set logged minutes ago must not
      // decide the weight of the next one
      if (!workout || workout._id === context.currentWorkoutId) continue;
      if (seenWorkoutIds.has(workout._id)) continue;
      seenWorkoutIds.add(workout._id);
      // every set of this exercise ANYWHERE in that session, not just the first entry's: a workout
      // can hold one exercise in two sub-slots, and reading only the first would drop the user's
      // heaviest set of the day. That would lower the ceiling on weaker evidence than they gave —
      // the wrong direction for a guard whose whole job is to not ask for too much.
      const sets = sessionSetsForExercise(this.getEffectiveExercises(workout, context.plan, PlanMerge.AsStored), uie.exercise._id);
      // a session that does not hold this exercise at all is not one of its sessions
      if (!sets.length) continue;
      recentSessions.push(sets);
    }
    // `bestStrengthSetAcrossSessions` applies the window itself, counting only sessions that yield a
    // usable set — which is why more than `PRESCRIPTION_CAP_SESSION_WINDOW` are handed over.
    const bestSet = bestStrengthSetAcrossSessions(
      recentSessions, context.fitnessState.weight_kg, uie.exercise?.bw_pc || 0,
    );
    return { read: RecentHistoryRead.Read, bestSet };
  }

  /** This exercise's recent workouts through the narrow read, newest first. Read once per load and
   *  shared (see `RecentWorkoutsRead`). A failed read is reported here, once, and answered as
   *  `Unreadable`; each caller then leaves its own step undone. The problem keeps the name it had
   *  when only the #5176 cap read this, so its Sentry group carries on. */
  private async readRecentWorkoutsAsync(uie: UiExercise): Promise<RecentWorkoutsRead> {
    if (!uie.exercise?._id) return { read: RecentHistoryRead.Unreadable };
    try {
      const workouts = await this.userDb.getRecentWorkoutsForExerciseAsync(
        uie.exercise._id, WorkoutLoaderService.MAX_SESSIONS_SCANNED_FOR_CAP);
      return { read: RecentHistoryRead.Read, workouts };
    } catch (err) {
      this.logging.trackSilentProblem('Issue #5176 - could not read history for the prescription cap', {
        exerciseId: uie.exercise._id,
        error: describeErrorForLog(err),
      });
      return { read: RecentHistoryRead.Unreadable };
    }
  }

  /**
   * Sets `uie.deloadCarriedOver` when the previous session of this exercise at this intensity ended
   * in an accepted deload that lowered today's target weight one step (#4822).
   *
   * Without it the lighter weight arrives with no explanation. On a one-set exercise that is the
   * WHOLE deload, since no light sets follow the failed one, and a user reading the lighter set as
   * "the deload set" stops at the rep target on purpose.
   *
   * Only for the session about to be performed, and only when it opens with an ordinary benchmark
   * set: a strength test is not "a normal working set". Which earlier session counts is
   * `isDeloadCarriedOverFromLastSession`'s to decide, and why a deload status alone is not proof is
   * `isSessionSteppedDownByDeload`'s.
   * The note is information only, so an unreadable history (already reported by the shared read)
   * just skips it.
   */
  private async applyDeloadCarriedOverAsync(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
    readRecentWorkoutsAsync: () => Promise<RecentWorkoutsRead>,
  ): Promise<void> {
    delete uie.deloadCarriedOver;

    const path = uie.planPath;
    if (mode !== WorkoutLoadMode.Current || !path || !uie.exercise?._id) return;
    if (uie.isTempSwap || uie.logExercise?.isTempSwap) return;
    if (path.nextWorkoutSetType !== SetType.Regular1st || uie.reducedTargetWeight_pc === -1) return;
    // cheap filter first: a path that never stepped down holds no figure to match
    if (path.lastDeload1rm_net_kg == null) return;
    const exerciseId = uie.exercise._id;

    const recent = await readRecentWorkoutsAsync();
    if (recent.read === RecentHistoryRead.Unreadable) return;

    // this exercise's entry in each earlier workout, newest first; the one being opened is left out
    const earlierEntries = recent.workouts
      .filter(workout => !!workout && workout._id !== context.currentWorkoutId)
      .map(workout => combinedEntryForExercise(this.getEffectiveExercises(workout, context.plan, PlanMerge.AsStored), exerciseId));
    uie.deloadCarriedOver = isDeloadCarriedOverFromLastSession(earlierEntries, path.intensityLevel, path.lastDeload1rm_net_kg);
  }

  /**
   * Load all exercises for a workout - creates UiExercise[] from Log_Workout
   * Returns { exercises, skippedExerciseIds, exercisesNeedingSwitch } so caller can handle errors
   */
  public async loadWorkoutExercisesAsync(
    logWorkout: Log_Workout,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): Promise<{ exercises: UiExercise[]; skippedExerciseIds: string[]; exercisesNeedingSwitch: UiExercise[]; loadInterrupted: boolean }> {
    // refuse a context missing one of the three documents this method and
    // loadExerciseAsync read, naming which — the declared type says all three are here,
    // but a caller assembling one from fields a lifecycle hook should have filled can
    // hand over undefined. Deliberately not DatabaseUnavailableError: nothing tried to
    // read. See design-docs/navigation.md, "Check the light half's data actually arrived".
    const missingDocuments = namesOfMissingWorkoutLoadingDocuments(context);
    if (missingDocuments.length > 0) {
      throw new Error(`loadWorkoutExercisesAsync: the workout loading context is missing ${missingDocuments.join(', ')}`);
    }

    const exercises: UiExercise[] = [];
    const skippedExerciseIds: string[] = [];
    const exercisesNeedingSwitch: UiExercise[] = [];
    let planFixupApplied = false;
    let workoutDocHealed = false;

    // #5640: settle which workout of the plan this is BEFORE anything is built from the
    // answer, and write it into the document in the same breath as the exercises below. The
    // three fields — name, planReference and exercises — used to be decided at different
    // moments, and the reporter's session is what happens when the answer changes in
    // between: his exercises came from Workout B and his label from Workout A. This also
    // widens the look-back when the cache cannot reach his previous workout, which the
    // synchronous accessor cannot do.
    // Repair a document whose stored reference its own logged sets contradict BEFORE anything
    // is read off that reference — the exercise merge in `getEffectiveExercises`, the combo-set
    // groups and the breadcrumb below. Only for the workout the user can train now: a past
    // session is judged against today's plan, which may not be the one it was trained under
    // (#6565).
    if (mayHealPlanReferenceInMode(mode)) this.healPlanReferenceContradictedByLoggedSets(logWorkout, context.plan);
    // #6850: a reference to a day since deleted from the plan builds no exercises at all. An
    // untrained workout forgets it here, so the code below works the day out again exactly as
    // for any unstamped workout
    if (mayHealPlanReferenceInMode(mode)) this.resetOutOfRangePlanReference(logWorkout, context.plan);

    const stampablePlanRef = logWorkout.planReference === undefined
      ? await this.resolvePlanReferenceForStampingAsync(logWorkout, context.plan)
      : logWorkout.planReference;

    // get effective exercises (computed on-demand if not already set). A past workout keeps its
    // stored list rather than taking on a plan changed after it was abandoned (#6574).
    const effectiveExercises = this.getEffectiveExercises(logWorkout, context.plan, planMergeForMode(mode));

    // find matching Plan_Workout to get combo set groupings
    // planReference is computed on-demand if not already set
    const effectivePlanRef = stampablePlanRef ?? this.getEffectivePlanReference(logWorkout, context.plan);
    const planWorkout = context.plan.workouts[effectivePlanRef];
    const comboSetGroups = planWorkout?.comboSetGroups;

    const isInMemory = !logWorkout._rev;

    // diagnostic breadcrumb for #2634 — captured by Sentry if an error fires later
    console.log('[loadWorkoutExercises]', {
      workoutId: logWorkout._id,
      isInMemory,
      mode,
      planRef: effectivePlanRef,
      exerciseCount: effectiveExercises.length,
      hadExercises: !!(logWorkout.exercises?.length),
      exerciseIds: effectiveExercises.map(e => e.exerciseId || '(none)'),
    });

    // if logWorkout.exercises was empty, link it to the effective exercises so modifications are persisted
    if (!logWorkout.exercises || logWorkout.exercises.length === 0) {
      logWorkout.exercises = effectiveExercises;

      // #5640: the exercises just came from `plan.workouts[effectivePlanRef]`, so this is the
      // one moment where the label and the exercise list are provably the same decision.
      // Stamp both here rather than leaving them to the first save, which re-asks the
      // question minutes later and can get a different answer.
      //
      // `decideWorkoutStamp` owns every condition — what is already on the document, whether
      // the look-back settled anything, and what the name should say. All this method
      // supplies is the one thing the decision cannot know: whether this is a workout the
      // user can actually train now. `Template` and `Unknown` sort above `Current` too, and
      // neither should be stamped.
      const stamp = decideWorkoutStamp(
        logWorkout.planReference,
        logWorkout.name,
        stampablePlanRef,
        stampablePlanRef === undefined ? undefined : context.plan.workouts[stampablePlanRef]?.name,
        mode <= WorkoutLoadMode.Current,
      );
      if (stamp.planReference !== undefined) {
        this.setWorkoutPlanReference(logWorkout, stamp.planReference);
      }
      if (stamp.name !== undefined) {
        this.setWorkoutName(logWorkout, stamp.name);
      }
    } else if (planMergeForMode(mode) === PlanMerge.WithCurrentPlan && effectiveExercises !== logWorkout.exercises) {
      // the merge with today's plan changed the list, and getEffectiveExercises does not write it
      // into the workout (#6585). this is the workout the user is about to train, so link it here:
      // the sets they log go into these entries, and a plan edit reaches the saved workout (#2786).
      // a past workout's stored list is never replaced (#6574).
      this.setWorkoutExercises(logWorkout, effectiveExercises);
    }

    // load all exercises in parallel (each operates on its own deep-cloned planSubSlot)
    let dbConnectionDied = false;
    const loadResults = await Promise.all(effectiveExercises.map(async (logExercise) => {
      const resolved = this.resolveWorkoutExerciseSubSlot(logExercise, context, mode);
      if (!resolved) {
        return null;
      }
      const planSubSlot = resolved.planSubSlot;

      try {
        const { uie, result } = await this.buildWorkoutUiExerciseAsync(
          logExercise, resolved, comboSetGroups, context, mode);

        if (result.sparseSetsHealed) {
          workoutDocHealed = true;
        }

        // if fixup corrected corrupted plan data, write fixed fields back to canonical plan.
        //
        // #4939: never from a finished session's own snapshot. `fixupInvalidPathState` repairs
        // the copy the page is holding, and on a finished workout that copy is a record of how
        // the exercise was set up back then — healing today's program from it would push a
        // years-old set type and rep target over the ones the user trains with now. The page's
        // own copy is still repaired, so the finished session draws correctly; only the write
        // into the program is skipped.
        if (result.pathFixupApplied && uie.canWritePlanSubSlotBackToPlan()) {
          planFixupApplied = true;
          const canonicalSubSlot = context.plan.subSlots[logExercise.subSlotId];
          if (canonicalSubSlot && uie.planPath) {
            const canonicalPath = canonicalSubSlot.exercisePaths.find(
              p => p.intensityLevel === uie.planPath.intensityLevel
            );
            if (canonicalPath) {
              canonicalPath.nextWorkoutSetType = uie.planPath.nextWorkoutSetType;
              canonicalPath.target_reps = uie.planPath.target_reps;
            }
          }
        }

        return { uie, needsSwitch: result.needsExerciseSwitch };
      } catch (err) {
        // if IDB connection died (iOS backgrounding), let recovery system handle it
        if (this.databaseRecovery.interceptDatabaseError(err)) {
          dbConnectionDied = true;
          return null;
        }
        // #3497: if a DB recovery is in flight, this error is almost certainly a
        // downstream symptom (a deeper helper swallowed an IDB error and returned
        // undefined → caller threw a TypeError in isSwitched/etc). Don't alarm the
        // user — the post-recovery notifyAllWatchersChanged() will retrigger the
        // workout load with fresh state. See plans/db-helper-idb-swallow-audit.md.
        // Track silently so the audit can measure prevalence and confirm the
        // suppression rate drops as helper sites are fixed (#39).
        if (this.databaseRecovery.isInDatabaseRecovery()) {
          const exerciseId = logExercise.exerciseId || planSubSlot.exercise_id;
          this.logging.trackSilentProblem('workout-load-error-during-db-recovery', {
            exerciseId,
            errName: err?.name,
            errMessage: err?.message,
          });
          dbConnectionDied = true;
          return null;
        }
        const exerciseId = logExercise.exerciseId || planSubSlot.exercise_id;
        err.message = `Exercise ${exerciseId} was skipped because it could not be loaded properly`;
        this.logging.trackAbnormalException(err);
        skippedExerciseIds.push(exerciseId);
        return null;
      }
    }));

    // collect results in order (preserves exercise ordering)
    for (const r of loadResults) {
      if (dbConnectionDied) break;
      if (!r) continue;
      exercises.push(r.uie);
      if (r.needsSwitch) {
        exercisesNeedingSwitch.push(r.uie);
      }
    }

    // if any plan paths were fixed, persist the corrections to CouchDB
    if (planFixupApplied) {
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    }

    // if any sparse sets were healed (#720), persist the workout doc once.
    // skip in-memory workouts (no _rev): the first save is owned by the page
    // (handles isInMemoryWorkout via ensureCachedReference + schedule wiring).
    // skip when the IDB connection died — recovery is already in flight.
    if (workoutDocHealed && logWorkout._rev && !dbConnectionDied) {
      try {
        await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), logWorkout);
      } catch (err) {
        // _rev conflict: a concurrent peer that won the race already wrote a
        // heal-or-no-op version, so dropping ours is fine. transient.
        if (isPouchConflict(err)) {
          console.log('Issue #720 - heal save lost rev race, peer save will heal', err);
        } else if (!this.databaseRecovery.interceptDatabaseError(err)) {
          // not IDB-died (recovery handled) and not a conflict — surface it
          throw err;
        }
      }
    }

    // loadInterrupted says the returned list is short (usually empty) because the
    // database went away mid-load — an IDB death, a recovery already in flight, or
    // the deliberate sign-out wipe. Recovery is handled elsewhere and reports for
    // itself, so a caller must not treat the short list as a fact about the
    // workout. Training's blank-day canary reads it for exactly that (#4410).
    return { exercises, skippedExerciseIds, exercisesNeedingSwitch, loadInterrupted: dbConnectionDied };
  }

  /**
   * Find the sub-slot a workout entry is drawn with, and report when the program no longer holds
   * it. Returns a copy the caller owns plus where it came from, or undefined when there is nothing
   * to draw the entry with.
   */
  private resolveWorkoutExerciseSubSlot(
    logExercise: Log_WorkoutExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): ResolvedWorkoutExerciseSubSlot | undefined {
    // #4614: an exercise the user removed in Edit Workouts has no entry in the program any
    // more, and this used to drop it from every finished session that contained it. Fall back
    // to the snapshot the workout log carries, or — for a session logged before snapshots
    // existed — to a sub-slot rebuilt from the rest of the log. resolveDisplaySubSlot owns
    // the rule and owns when the rebuild refuses.
    //
    // #4939: on a FINISHED workout the snapshot is preferred over the live entry, so that a
    // later program edit cannot change what an old session shows. Only Past asks for this —
    // a session still in progress needs today's entry, because concluding its first set
    // writes progression into this object and then attaches it to the program.
    const resolved = resolveDisplaySubSlot(
      context.plan.subSlots, logExercise, mode === WorkoutLoadMode.Past);
    if (resolved.missingFromPlan) {
      reportDisplaySubSlotMissingFromPlan(
        logExercise.subSlotId, resolved.source, this.logging, 'loadWorkoutExercises', logExercise);
    }
    const planSubSlot = deepClone(resolved.subSlot);
    if (!planSubSlot) {
      return undefined;
    }
    return {
      planSubSlot,
      missingFromPlan: resolved.missingFromPlan,
      // #4939: a finished session's own record rather than a copy of what the program says today
      isHistorical: resolved.source === DisplaySubSlotSource.LogSnapshot && mode === WorkoutLoadMode.Past,
    };
  }

  /**
   * Build and fully load the UiExercise for one workout entry — the body of
   * `loadWorkoutExercisesAsync`'s per-exercise step, shared with `loadSingleWorkoutExerciseAsync`.
   * Throws whatever `loadExerciseAsync` throws; the callers decide what a failure means.
   */
  private async buildWorkoutUiExerciseAsync(
    logExercise: Log_WorkoutExercise,
    resolved: ResolvedWorkoutExerciseSubSlot,
    comboSetGroups: { [subSlotId: string]: number } | undefined,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): Promise<{ uie: UiExercise; result: LoadExerciseResult }> {
    const uie = new UiExercise();
    uie.planSubSlot = resolved.planSubSlot;
    // remember that the program has no entry for this exercise any more, so nothing
    // downstream writes this historical copy back into the plan (#4614).
    uie.planSubSlotMissingFromPlan = resolved.missingFromPlan;
    // #4939: and remember that this copy is a finished session's own record rather than a
    // copy of what the program says today, so nothing writes it back over the live entry.
    uie.planSubSlotIsHistorical = resolved.isHistorical;
    uie.logExercise = logExercise;

    // set combo set grouping from plan
    if (comboSetGroups && comboSetGroups[logExercise.subSlotId] !== undefined) {
      uie.comboSetNumber = comboSetGroups[logExercise.subSlotId];
    }

    const result = await this.loadExerciseAsync(uie, context, mode);

    // if this was a temp swap, restore temp swap state (planPath was incorrectly set by loadExerciseAsync)
    if (logExercise.isTempSwap) {
      uie.isTempSwap = true;
      uie.tempSwapOriginalExerciseId = logExercise.tempSwapOriginalExerciseId;
      this.clearTempSwapProgression(uie);
      uie.badges = this.getExerciseBadges(uie, context.declaredState, context.fitnessState);
    }

    return { uie, result };
  }

  /**
   * Build the UiExercise for ONE entry of a workout that is already on screen, the same way
   * `loadWorkoutExercisesAsync` builds each of its entries. Used for the entry an exercise change
   * splits off (#5507), which appears mid-session and must not cost the page a full reload — a
   * reload builds new objects for every exercise and loses what the screen holds, including the
   * sub-slot ids a switch retired.
   *
   * Throws when the entry cannot be drawn at all, so the caller can report it rather than show a
   * workout silently missing an exercise.
   */
  public async loadSingleWorkoutExerciseAsync(
    logWorkout: Log_Workout,
    logExercise: Log_WorkoutExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): Promise<UiExercise> {
    const resolved = this.resolveWorkoutExerciseSubSlot(logExercise, context, mode);
    if (!resolved) {
      throw new Error(`loadSingleWorkoutExerciseAsync: nothing to draw workout entry ${logExercise.subSlotId} with`);
    }
    const planWorkout = context.plan.workouts[this.getEffectivePlanReference(logWorkout, context.plan)];
    const { uie } = await this.buildWorkoutUiExerciseAsync(
      logExercise, resolved, planWorkout?.comboSetGroups, context, mode);
    return uie;
  }

  /**
   * Rebuild sets data for an existing UiExercise - used when sets need to be recalculated
   * Mutates uie.sets, uie.description, and uie.badges directly.
   * Caller handles UI concerns (loading state, change detection).
   */
  public rebuildSetsData(
    uie: UiExercise,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): void {
    const { declaredState, fitnessState, locale } = context;

    // build and assign the sets
    this.buildAndAssignSets(uie, declaredState, fitnessState, mode);

    // build and assign the badges and the header line
    this.applyBadgesAndDescription(uie, declaredState, fitnessState, mode, locale);
  }

  /**
   * Skip avoided exercises in the slot.
   *
   * Two reasons to swap subSlot.exercise_id:
   * 1. The currently-bound UDE is `virtuallyDeleted` (e.g. user deleted it from the
   *    picker — the doc may still exist in userdb-* as a soft-delete to preserve the
   *    Revert chain). Walk the lineage chain via findNearestLiveAncestorAsync to find
   *    the nearest live ancestor. This handles forks of non-primary slot exercises
   *    correctly (suggestAvailableExercise's "not in slot.exercises → fall back to
   *    index 0" path would wrongly swap to the slot primary in that case).
   * 2. Avoided / quarantined rotation per suggestAvailableExercise.
   *
   * Both swaps are the app's own choice, so both go through rotateExerciseInSubSlotByApp:
   * the rebuild warning must not count them as the user customizing their program.
   */
  private async skipAvoidedExercises(
    slot: ExerciseSlot,
    planSubSlot: Plan_SubSlot,
    fitnessState: FitnessState,
    plan: FitnessPlan,
    nowLocal: DateTime,
    watcher: DocWatcher,
  ): Promise<void> {
    // if the bound exercise is a virtually-deleted UDE, fall back to lineage ancestor
    if (planSubSlot.exercise_id && isCustomExerciseById(planSubSlot.exercise_id)) {
      try {
        const ude = await this.userDb.getCustomExerciseByIdAsync(planSubSlot.exercise_id);
        if (ude.virtuallyDeleted) {
          const liveAncestorId = await findNearestLiveAncestorAsync(
            planSubSlot.exercise_id,
            this.userDb,
            this.exerciseDb,
            watcher,
          );
          if (liveAncestorId && liveAncestorId !== planSubSlot.exercise_id) {
            rotateExerciseInSubSlotByApp(planSubSlot, liveAncestorId);
          }
          // if no live ancestor found, leave to suggestAvailableExercise below
        }
      } catch (err) {
        if (!isPouchNotFound(err)) throw err;
        // UDE was hard-deleted — leave to suggestAvailableExercise below
      }
    }

    const suggested = suggestAvailableExercise(
      fitnessState,
      plan,
      slot,
      planSubSlot.startIndex,
      planSubSlot.exercise_id,
      nowLocal.toUTC(),
    );
    // only invoke the canonical swap helper on a real change — suggestAvailableExercise
    // returns the current id when no avoid/quarantine rotation applies, and the helper's
    // full reset would otherwise wipe valid working state on every workout load.
    if (suggested !== planSubSlot.exercise_id) {
      rotateExerciseInSubSlotByApp(planSubSlot, suggested);
    }
  }

  /**
   * Build sets for an exercise (Myo-Reps or regular sets)
   */
  private buildSets(
    tempUie: UiExercise,
    logExercise: Log_WorkoutExercise,
    planSubSlot: Plan_SubSlot,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    mode: WorkoutLoadMode
  ): UiSet[] {
    const sets: UiSet[] = [];

    // Check if Myo-Reps will be used
    const useMyoReps = this.willUseMyoReps(tempUie, declaredState, fitnessState);
    // #3354: for past (locked) workouts, pin the prescribed set count to the
    // per-workout snapshot captured when the first set was concluded
    // (planSubslotBackup) instead of the live plan — otherwise a later
    // Edit-Workouts volume increase retroactively manufactures empty placeholder
    // sets and flips a completed past workout back to in-progress.
    // setCurrentPath() already reads this same backup for intensity/paths
    // (whenever it exists); here we read it for the set count too, but only in
    // Past mode so the active (Current/Future) workout keeps reading the live
    // plan and legitimately added volume still surfaces an extra set to perform.
    // #4939 made this redundant rather than wrong: on a finished workout `planSubSlot` is now
    // already a copy of the very snapshot named here, so both arms carry the same number. It is
    // kept because it is the #3354 guard, and #3354 is what put empty sets into finished
    // sessions and flipped them back to unfinished — if `preferSnapshot` is ever narrowed, this
    // is the line that keeps that from coming back.
    const countSubSlot = mode === WorkoutLoadMode.Past && logExercise.planSubslotBackup
      ? logExercise.planSubslotBackup
      : planSubSlot;
    let numPlannedSets = countSubSlot.defaultVolume_sets;

    if (useMyoReps) {
      // Myo-Reps set setup
      const intensity_pc = tempUie.planPath && getSlotPathIntensity(planSubSlot, tempUie.planPath);
      let numMyoReps = 0;
      if (tempUie.planPath) {
        if (tempUie.planPath.nextWorkoutSetType === SetType.RepTest) {
          numMyoReps = getMyoRepsForIntensity(intensity_pc);
        } else if (tempUie.planPath.nextWorkoutSetType === SetType.NonRMTest) {
          numMyoReps = getMyoRepsForTargetRepsCount(getRepTargetForIntensity(intensity_pc));
        } else {
          numMyoReps = getMyoRepsForTargetRepsCount(tempUie.planPath.target_reps);
        }
      }

      const miniSetsPerSet: number[] = getMyoSetsFromSets(numPlannedSets);
      // #4614: never draw fewer sets than the workout actually holds. This is the floor the
      // regular branch below already has, and the Myo branch needs it for the same reason:
      // getMyoSetsFromSets maps a PRESCRIBED set count to how many Myo sets that becomes, so
      // it answers correctly only when `numPlannedSets` really is the prescribed count. A
      // sub-slot rebuilt by `rebuildDisplaySubSlotFromLog` takes that count from the log,
      // where a Myo session stores the already-mapped number — 12 prescribed sets are stored
      // as the 3 that were performed, and mapping 3 again gives 1. Without this floor two of
      // the user's three performed sets would silently vanish from the session, which is a
      // smaller copy of the bug this whole path exists to fix.
      const numSets = Math.max(miniSetsPerSet.length, logExercise.sets?.length || 0);
      for (let setIndex = 0; setIndex < numSets; setIndex++) {
        const uis = new UiSet();
        // only read when a set has to be invented below, and an index past the mapping's end
        // is by definition one the log already holds — so it never needs a mini-set count
        const numMiniSets = miniSetsPerSet[setIndex] ?? 0;

        let logSet = logExercise.sets?.[setIndex];
        if (!logSet) {
          logSet = {
            state: LogRecordState.Scheduled,
            stats: {},
            setSuccess: undefined,
            miniSets: [],
            preStatus: [],
            postStatus: []
          } as Log_WorkoutSet;
          for (let i = 0; i < numMiniSets; i++) {
            logSet.miniSets[i] = { plannedReps: numMyoReps, reps: numMyoReps };
          }
        }
        uis.logSet = logSet;
        uis.uiExercise = tempUie;
        sets.push(uis);
      }
    } else {
      // Regular set setup
      // read before the branch below overwrites it, so the report at the end of
      // this block says what the workout actually held rather than echoing the
      // count just derived from it
      const storedNumPlannedSets = logExercise.numPlannedSets;
      if (logExercise.numPlannedSets) {
        numPlannedSets = logExercise.numPlannedSets;
      } else if (tempUie.planPath && tempUie.planPath.volumeAdjustment) {
        if (tempUie.planPath.volumeAdjustment < 0 || (logExercise.sets?.length || 0) === 0) {
          numPlannedSets += tempUie.planPath.volumeAdjustment;
        }
        logExercise.numPlannedSets = numPlannedSets;
      }

      // never plan an exercise with no sets: it makes a finished workout
      // unpromotable and silent. Why that costs what it does, and why this guards
      // the whole branch, are in design-docs/volume-autoregulation.md
      //
      // the test is "not at least 1" rather than "less than 1" BECAUSE OF the
      // absent case, and swapping it back silently reopens that road: neither
      // client nor shared has strictNullChecks, so an absent defaultVolume_sets
      // really can arrive here, and both `undefined < 1` and `NaN < 1` (which is
      // what the adjustment above makes of an absent value) are FALSE. The count
      // would then flow on as NaN, Math.max returns NaN, the loop below never
      // runs, and the exercise is built with no sets at all — past this guard
      // and unreported.
      if (!(numPlannedSets >= 1)) {
        this.logging.trackSilentProblem('workout-loader-zero-planned-sets', {
          subSlotId: logExercise.subSlotId,
          exerciseId: tempUie.exercise?._id,
          defaultVolume_sets: countSubSlot.defaultVolume_sets,
          storedNumPlannedSets,
          volumeAdjustment: tempUie.planPath?.volumeAdjustment,
          intensityLevel: tempUie.planPath?.intensityLevel,
        });
        numPlannedSets = 1;
        // keep the corrected count, so re-opening this workout neither derives
        // the empty count again nor reports it again
        logExercise.numPlannedSets = numPlannedSets;
      }

      const numSets = Math.max(logExercise.sets?.length || 0, numPlannedSets);
      for (let setIndex = 0; setIndex < numSets; setIndex++) {
        const uis = new UiSet();

        let logSet = logExercise.sets?.[setIndex];
        if (!logSet) {
          logSet = {
            state: LogRecordState.Scheduled,
            stats: {},
            setSuccess: undefined,
            preStatus: [],
            postStatus: []
          } as Log_WorkoutSet;
        }
        uis.logSet = logSet;
        uis.uiExercise = tempUie;
        sets.push(uis);
      }
    }

    return sets;
  }

  // ============================================================
  // Pure helper functions (will be moved from workout-detail.ts)
  // ============================================================

  /**
   * Determine if Myo-Reps will be used for an exercise
   */
  public willUseMyoReps(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState
  ): boolean {
    // if planPath is missing
    if (!uie.planPath) {
      return false;
    }

    // check if exercise is configured for Myo-Reps
    const numPlannedSets = uie.planSubSlot.defaultVolume_sets;
    const exerciseUseMyoReps = exerciseUsesMyoReps(uie.exercise, numPlannedSets, declaredState, fitnessState);

    // if not configured for myo-reps or is RM test
    if (!exerciseUseMyoReps || uie.planPath.nextWorkoutSetType === SetType.RMTest) {
      return false;
    }

    const intensity_pc = getSlotPathIntensity(uie.planSubSlot, uie.planPath);

    // calculate numMyoReps based on set type
    let numMyoReps = 0;
    if (uie.planPath.nextWorkoutSetType === SetType.RepTest) {
      numMyoReps = getMyoRepsForIntensity(intensity_pc);
    } else if (uie.planPath.nextWorkoutSetType === SetType.NonRMTest) {
      numMyoReps = getMyoRepsForTargetRepsCount(getRepTargetForIntensity(intensity_pc));
    } else {
      numMyoReps = getMyoRepsForTargetRepsCount(uie.planPath.target_reps);
    }

    // check all conditions
    if (numMyoReps <= 0) {
      return false;
    }

    if (uie.planPath.nextWorkoutSetType === SetType.RepTest) {
      return intensity_pc <= 80;
    } else if (uie.planPath.nextWorkoutSetType === SetType.NonRMTest) {
      return getRepTargetForIntensity(intensity_pc) >= 8;
    } else {
      return uie.planPath.target_reps >= 8;
    }
  }

  /**
   * Get badges for exercise display
   */
  private getExerciseBadges(
    e: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState
  ): ExerciseBadge[] {
    const badges: ExerciseBadge[] = [];

    // if temp swap, show badge and skip other badges (except bodyweight)
    if (e.isTempSwap) {
      badges.push({
        label: LL.workouts.detail.badges.tempSwap(),
        color: BadgeColor.Blue,
        info: LL.workouts.detail.badges.tempSwapInfo(),
      });
      if (e.exercise.bw_pc > 0) {
        badges.push({
          label: LL.workouts.detail.badges.bodyweight(),
          color: BadgeColor.Purple,
          info: LL.workouts.detail.badges.bodyweightInfo(),
        });
      }
      return badges;
    }

    // check for Myo-Reps - only show if they will ACTUALLY be used
    if (this.willUseMyoReps(e, declaredState, fitnessState)) {
      badges.push({
        label: LL.workouts.detail.badges.myoReps(),
        color: BadgeColor.Yellow,
        info: LL.workouts.detail.badges.myoRepsInfo(),
        helpId: '@help:workout.myoreps'
      });
    }

    // determine set type from firstSet.logSet or planPath as fallback
    let setType: SetType | undefined;

    if (e.sets.length > 0 && e.sets[0].logSet && e.sets[0].logSet.setType) {
      setType = e.sets[0].logSet.setType;
    } else if (e.planPath) {
      setType = e.planPath.nextWorkoutSetType;
    }

    // bodyweight exercise badge
    if (e.exercise.bw_pc > 0) {
      badges.push({
        label: LL.workouts.detail.badges.bodyweight(),
        color: BadgeColor.Purple,
        info: LL.workouts.detail.badges.bodyweightInfo(),
      });
    }

    if (setType === SetType.RMTest) {
      badges.push({
        label: LL.workouts.detail.badges.strengthTest(),
        color: BadgeColor.Green,
        info: LL.workouts.detail.badges.strengthTestInfo(),
        helpId: '@help:workout.RM-strength-test'
      });
    } else if (setType === SetType.RepTest) {
      badges.push({
        label: LL.workouts.detail.badges.repTest(),
        color: BadgeColor.Green,
        info: LL.workouts.detail.badges.repTestInfo(),
        helpId: '@help:workout.rep-test'
      });
    }

    return badges;
  }

  /**
   * Build exercise description for display in header
   * Returns description text and flag indicating if exercise switch is needed
   */
  private buildExerciseDescription(
    uie: UiExercise,
    mode: WorkoutLoadMode,
    locale: LocaleSettings
  ): { description: string; needsExerciseSwitch: boolean; updatedTargetWeight_kg?: number } {
    uie.hasPeriodizationDescription = false;
    if (uie.sets[0] == null) {
      return { description: '', needsExerciseSwitch: false };
    }

    let h = '';
    let needsExerciseSwitch = false;
    let updatedTargetWeight_kg: number | undefined;

    // don't try to show weights and other details in template mode
    if (mode === WorkoutLoadMode.Template) {
      const setWord = uie.sets.length === 1
        ? LL.workouts.detail.exerciseHeader.set()
        : LL.workouts.detail.exerciseHeader.sets();
      h += `${uie.sets.length} ${setWord}`;
      return { description: h, needsExerciseSwitch: false };
    }

    let showSetsCount = true;
    if (uie.logExercise.sets && uie.logExercise.sets[0] && [LogRecordState.Done, LogRecordState.Incomplete].includes(uie.logExercise.sets[0].state)) {
      h = formatExerciseSets(uie.logExercise, locale, false);
      showSetsCount = false;
    } else if (uie.logExercise.sets && uie.logExercise.sets[0] && [LogRecordState.Skipped].includes(uie.logExercise.sets[0].state)) {
      h = LL.workouts.detail.exerciseHeader.skipped();
      showSetsCount = false;
    } else if (
      uie.planSubSlot.rebuildPathsForIntensity ||
      (uie.planPath &&
        (uie.planPath.nextWorkoutSetType === SetType.RMTest || uie.planPath.nextWorkoutSetType === SetType.NonRMTest)) ||
      uie.reducedTargetWeight_pc === -1
    ) {
      if (uie.planPath?.nextWorkoutSetType === SetType.NonRMTest) {
        let intensity = uie.planPath.intensityLevel;
        if (intensity === IntensityLevel.DUPlight || intensity === IntensityLevel.TUPlight) {
          h += LL.workouts.detail.exerciseHeader.highRep();
          uie.hasPeriodizationDescription = true;
        } else if (intensity === IntensityLevel.DUPheavy || intensity === IntensityLevel.TUPheavy) {
          h += LL.workouts.detail.exerciseHeader.heavy();
          uie.hasPeriodizationDescription = true;
        } else if (intensity === IntensityLevel.TUPmedium) {
          h += LL.workouts.detail.exerciseHeader.medium();
          uie.hasPeriodizationDescription = true;
        }
        // trim trailing "; " from intensity prefix (test info shown via badge)
        h = h.replace(/;\s*$/, '');
      }
      // show sets count
    } else if (uie.planPath) {
      let intensity = uie.planPath.intensityLevel;
      if (intensity === IntensityLevel.DUPlight || intensity === IntensityLevel.TUPlight) {
        h = LL.workouts.detail.exerciseHeader.highRep();
        uie.hasPeriodizationDescription = true;
      } else if (intensity === IntensityLevel.DUPheavy || intensity === IntensityLevel.TUPheavy) {
        h = LL.workouts.detail.exerciseHeader.heavy();
        uie.hasPeriodizationDescription = true;
      } else if (intensity === IntensityLevel.TUPmedium) {
        h = LL.workouts.detail.exerciseHeader.medium();
        uie.hasPeriodizationDescription = true;
      }
      let unit = uie.stat.weight_unit;
      // round target weight to current equipment config
      let { weight_kg } = adjustWeight(uie.targetWeight_kg, 'round', uie.equipment);
      if (uie.planPath.reachedMaxWeightConfiguration) {
        let { weight_kg: newTarget } = adjustWeight(uie.targetWeight_kg, 'step_up', uie.equipment);
        // staleness is judged on the plan's stored target, not this session's weight: a layoff lowers
        // only the session weight, and must not wipe a marker the stored target still sits at
        const { weight_kg: storedTarget_kg } = adjustWeight(uie.planPath.target_weight_kg, 'round', uie.equipment);
        const markerAction = decideMaxedOutMarkerAtLoad(uie.planPath.reachedMaxWeightConfiguration, storedTarget_kg, newTarget);
        if (markerAction === MaxedOutMarkerAction.SwitchExercise) {
          // exercise needs to be replaced - flag it for the page to handle
          needsExerciseSwitch = true;
          return { description: '', needsExerciseSwitch, updatedTargetWeight_kg };
        } else if (markerAction === MaxedOutMarkerAction.StepUp) {
          updatedTargetWeight_kg = newTarget;
          weight_kg = newTarget;
        }
        // if the marker is stale (the target moved below it), it is dropped and the target stays
        uie.planPath.reachedMaxWeightConfiguration = undefined;
      }
      let reps = uie.planPath.target_reps;
      // check if mode is editable (not Past)
      const isEditableMode = mode !== WorkoutLoadMode.Past;
      if (typeof reps !== 'number') {
        h += formatMeasure(weight_kg, 'addedweight_kg', locale, unit);
      } else if (isEditableMode) {
        h += `${formatMeasure(weight_kg, 'addedweight_kg', locale, unit)} ${Formatting.times} ${reps}`;
      }
    }

    if (uie.sets.length >= 1 && showSetsCount) {
      const setWord = uie.sets.length === 1
        ? LL.workouts.detail.exerciseHeader.set()
        : LL.workouts.detail.exerciseHeader.sets();
      if (h) {
        h += ' | ';
      }
      h += `${uie.sets.length} ${setWord}`;
    }

    return { description: h, needsExerciseSwitch, updatedTargetWeight_kg };
  }

  /**
   * Select current path based on planSubSlot intensity
   */
  public setCurrentPath(uie: UiExercise): void {
    if (uie.logExercise.planSubslotBackup) {
      uie.planPath =
        uie.logExercise.planSubslotBackup.nextWorkoutIntensity &&
        uie.logExercise.planSubslotBackup.exercisePaths.find(p => p.intensityLevel === uie.logExercise.planSubslotBackup.nextWorkoutIntensity);
    } else {
      uie.planPath =
        uie.planSubSlot.nextWorkoutIntensity &&
        uie.planSubSlot.exercisePaths.find(p => p.intensityLevel === uie.planSubSlot.nextWorkoutIntensity);
    }

    if (!uie.planPath) {
      uie.planPath = uie.planSubSlot.exercisePaths[0];
    }
  }

  /**
   * Fix invalid plan path states where Regular1st (or other non-test types)
   * have missing target_reps or target_weight_kg
   */
  public fixupInvalidPathState(
    uie: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState
  ): boolean {
    if (!uie.planPath) {
      return false;
    }

    // only fix paths that are supposed to carry a target at all. The shared set is read rather
    // than listed here because `rebuildExercisePathFromLog` (`client/src/util/historical-subslot.ts`)
    // has to predict this early return exactly, and a copy of the list in either place would be
    // free to drift away from the other with nothing noticing.
    if (setTypeHasNoTargetPrescription(uie.planPath.nextWorkoutSetType)) {
      return false;
    }

    // use Number.isFinite (not typeof === 'number') so NaN counts as missing, matching the
    // source guard `resolveEstablishedSetType`; otherwise the two enforcement points disagree
    // on NaN and a NaN-valued path would be neither healed here nor caught at the source.
    const hasTargetReps = Number.isFinite(uie.planPath.target_reps);
    const hasTargetWeight = Number.isFinite(uie.planPath.target_weight_kg);
    const usesRMTesting = exerciseUsesRMTesting(uie.exercise, declaredState, fitnessState);

    // if everything is valid, no need to fix
    if (hasTargetReps && hasTargetWeight) {
      return false;
    }

    // Track that we encountered an invalid state and then heal it below. This is the
    // downstream canary: the bad path was created earlier at set conclusion (see the
    // source guard `establishWorkingPath` in workout-detail.ts). It is kept on purpose,
    // tagged with mh-physique #3752, so we can watch the volume decay over a few weeks
    // as legacy corrupted paths self-heal on load and the source guard stops new ones.
    // The (#3752) suffix re-fingerprints this from the old Sentry issue 2YK on purpose.
    this.logging.trackSilentProblem('Invalid plan path state detected (#3752)', {
      exerciseId: uie.exercise._id,
      exerciseName: uie.exercise.name,
      nextWorkoutSetType: uie.planPath.nextWorkoutSetType,
      hasTargetReps,
      hasTargetWeight,
      usesRMTesting,
      intensityLevel: uie.planPath.intensityLevel,
    });

    // missing both - force full retest
    if (!hasTargetReps && !hasTargetWeight) {
      uie.planPath.nextWorkoutSetType = usesRMTesting ? SetType.RMTest : SetType.NonRMTest;
      if (!usesRMTesting) {
        const intensity_pc = getSlotPathIntensity(uie.planSubSlot, uie.planPath);
        uie.planPath.target_reps = getRepTargetForIntensity(intensity_pc);
      }
      return true;
    }

    // missing weight - force retest (weight is critical)
    if (!hasTargetWeight) {
      uie.planPath.nextWorkoutSetType = usesRMTesting ? SetType.RMTest : SetType.NonRMTest;
      return true;
    }

    // missing reps only
    if (!hasTargetReps) {
      if (usesRMTesting) {
        // for RM testing exercises, force RepTest to measure reps properly
        uie.planPath.nextWorkoutSetType = SetType.RepTest;
      } else {
        // for non-RM exercises, calculate reps from intensity
        const intensity_pc = getSlotPathIntensity(uie.planSubSlot, uie.planPath);
        uie.planPath.target_reps = getRepTargetForIntensity(intensity_pc);
        // also save to main plan so it persists
        const mainPath = uie.planSubSlot.exercisePaths.find(
          p => p.intensityLevel === uie.planPath.intensityLevel
        );
        if (mainPath) {
          mainPath.target_reps = uie.planPath.target_reps;
        }
      }
      return true;
    }

    return false;
  }

  /**
   * Rebuild exercise paths when changing intensity was scheduled, or paths are missing
   */
  public rebuildPathsIfNeeded(
    e: UiExercise,
    declaredState: DeclaredState,
    fitnessState: FitnessState
  ): void {
    // this function only handles cases when changing intensity was scheduled, or paths are missing
    if (!e.planSubSlot.exercisePaths || e.planSubSlot.exercisePaths.length < 1) {
      e.planSubSlot.rebuildPathsForIntensity = e.planSubSlot.rebuildPathsForIntensity || IntensityLevel.Initial;
    } else if (e.reducedTargetWeight_pc === -1 || e.resetProgress) {
      if (e.reducedTargetWeight_pc === -1 && e.sets.length > 0 && e.sets[0].logSet) {
        e.sets[0].logSet.preStatus.push({ status: PreSetStatus.DidNotPerformRetest, textInserts: undefined });
      }
      const numPaths = e.planSubSlot.exercisePaths.length;
      if (numPaths === 2 && e.planSubSlot.intensities[e.planSubSlot.trainingLevel][IntensityLevel.DUPheavy]) {
        e.planSubSlot.rebuildPathsForIntensity = IntensityLevel.DUPheavy;
      } else if (numPaths === 3 && e.planSubSlot.intensities[e.planSubSlot.trainingLevel][IntensityLevel.TUPheavy]) {
        e.planSubSlot.rebuildPathsForIntensity = IntensityLevel.TUPheavy;
      } else {
        e.planSubSlot.rebuildPathsForIntensity = e.planSubSlot.nextWorkoutIntensity;
      }
    }
    if (!e.planSubSlot.rebuildPathsForIntensity) {
      return;
    }

    // always rebuild everything from scratch - no old data is relevant. Phase 2 model:
    // user's stated rep-target preference lives on ExerciseStat.preferRepTargetByIntensity, so
    // it is unaffected by wiping exercisePaths. The previous preservation map projected
    // originalTargetReps from old paths to new ones; that role is now served by reading
    // from ExerciseStat below at each new-path construction.
    const exerciseStat = fitnessState.exerciseStats[e.planSubSlot.exercise_id];
    e.planSubSlot.exercisePaths = [];

    // simple intensification
    if (
      e.planSubSlot.rebuildPathsForIntensity === IntensityLevel.Initial ||
      e.planSubSlot.rebuildPathsForIntensity === IntensityLevel.Intensified
    ) {
      const newPath: Plan_ExercisePath = {
        intensityLevel: e.planSubSlot.rebuildPathsForIntensity,
        nextWorkoutSetType: exerciseUsesRMTesting(e.exercise, declaredState, fitnessState) ? SetType.RMTest : SetType.NonRMTest,
      };
      const preferReps = exerciseStat?.preferRepTargetByIntensity?.[newPath.intensityLevel];
      if (newPath.nextWorkoutSetType === SetType.NonRMTest) {
        newPath.target_reps = preferReps ?? getRepTargetForIntensity(getSlotPathIntensity(e.planSubSlot, newPath));
      }
      e.planSubSlot.exercisePaths.push(newPath);
      e.planSubSlot.nextWorkoutIntensity = e.planSubSlot.rebuildPathsForIntensity;
      delete e.planSubSlot.rebuildPathsForIntensity;
      return;
    }

    let pathIntensities = [];

    if (
      e.planSubSlot.rebuildPathsForIntensity >= IntensityLevel.DUPlight &&
      e.planSubSlot.rebuildPathsForIntensity <= IntensityLevel.DUPheavy
    ) {
      pathIntensities = [IntensityLevel.DUPheavy, IntensityLevel.DUPlight];
    } else if (
      e.planSubSlot.rebuildPathsForIntensity >= IntensityLevel.TUPlight &&
      e.planSubSlot.rebuildPathsForIntensity <= IntensityLevel.TUPheavy
    ) {
      pathIntensities = [IntensityLevel.TUPheavy, IntensityLevel.TUPmedium, IntensityLevel.TUPlight];
    }
    const usesRM = exerciseUsesRMTesting(e.exercise, declaredState, fitnessState);
    for (let i of pathIntensities) {
      const newPath: Plan_ExercisePath = {
        intensityLevel: i,
        nextWorkoutSetType: usesRM ? SetType.RMTest : SetType.NonRMTest,
      };
      const preferReps = exerciseStat?.preferRepTargetByIntensity?.[i];
      if (!usesRM) {
        newPath.target_reps = preferReps ?? getRepTargetForIntensity(getSlotPathIntensity(e.planSubSlot, newPath));
      }
      e.planSubSlot.exercisePaths.push(newPath);
    }
    delete e.planSubSlot.rebuildPathsForIntensity;
    e.planSubSlot.nextWorkoutIntensity = pathIntensities[0];
  }

  /**
   * Determine workout mode based on workout state and surrounding workouts.
   * This is the unified logic used by both workout-detail and training pages.
   */
  async getWorkoutModeAsync(
    logWorkout: Log_Workout,
    userDb: UserDb,
    nowLocal: DateTime,
    watcher: DocWatcher
  ): Promise<WorkoutLoadMode> {
    const result = await this.getWorkoutModeWithContextAsync(logWorkout, userDb, nowLocal, watcher);
    return result.mode;
  }

  /**
   * Determine workout mode and return context about current workout.
   * Use this when you need to know which workout is "current" (e.g., for SoftFuture warning).
   */
  async getWorkoutModeWithContextAsync(
    logWorkout: Log_Workout,
    userDb: UserDb,
    nowLocal: DateTime,
    watcher: DocWatcher
  ): Promise<WorkoutLoadModeResult> {
    // answer without a database read where this workout alone settles it — see
    // resolveWorkoutLoadModeWithoutNeighbours for why that check comes first
    const withoutNeighbours = resolveWorkoutLoadModeWithoutNeighbours(logWorkout, nowLocal);
    if (withoutNeighbours !== undefined) {
      return { mode: withoutNeighbours };
    }

    // get workouts around this one (±14 days, capped at today)
    const workoutDay = getEntryLocalTimeInDesiredTimeZone(logWorkout, nowLocal).startOf('day');
    const todayDay = nowLocal.startOf('day');
    const startDay = workoutDay.minus({ days: 14 }).startOf('day');
    let endDay = workoutDay.plus({ days: 14 }).startOf('day');
    if (endDay >= todayDay) {
      endDay = todayDay;
    }

    const workoutsAroundThis = (await userDb.getLogEntriesInRangeAsync(startDay, endDay.endOf('day'), watcher))
      .filter(entry => entry.type === LogRecordType.Workout) as Log_Workout[];

    return resolveWorkoutLoadModeFromNeighbours(logWorkout, workoutsAroundThis, nowLocal);
  }

  /**
   * Get a replacement exercise when current one needs to be switched.
   * Marks the current exercise stat as switched/quarantined and suggests a new one.
   */
  async getReplacementExerciseAsync(
    e: UiExercise,
    mode: 'switched' | 'plateau' | 'weakness',
    fitnessState: FitnessState,
    plan: FitnessPlan,
    nowLocal: DateTime,
    watcher: DocWatcher,
  ): Promise<string> {
    const utc_now = nowLocal.toUTC();
    if (e.stat) {
      if (mode === 'plateau' || mode === 'weakness') {
        ExerciseStat.markQuarantined(e.stat, utc_now, mode);
      } else {
        ExerciseStat.markSwitched(e.stat, utc_now);
      }
    }

    let exerciseToAvoid: string[] = [];
    // if the exercise sits under the `slot/$custom` stand-in it has no slot, so there are no
    // alternatives to steer away from; suggestAvailableExercise then keeps the current exercise
    if (mode === 'weakness' && e.exercise && e.exercise.bw_pc && e.slot) {
      for (let i = 0; i < e.slot.exercises.length; i++) {
        const exerciseId = e.slot.exercises[i].exerciseId;
        // the reporting read on purpose: a candidate whose read failed is only not avoided,
        // which is milder than abandoning the switch (df-c21e11979ef2)
        const exercise = await this.exerciseDb.getGlobalExerciseByIdAsync(exerciseId, watcher);
        if (exercise?.bw_pc !== undefined) {
          exerciseToAvoid.push(exerciseId);
        }
      }
    }

    return suggestAvailableExercise(
      fitnessState,
      plan,
      e.slot,
      e.planSubSlot.startIndex,
      e.exercise && e.exercise._id,
      utc_now,
      exerciseToAvoid,
    );
  }

  /**
   * #6063: the replacement for an exercise switched away at the rep ceiling (#5979), preferring one
   * that does not train on the same equipment — the next weight on it was too big a jump, so a
   * replacement on it would put the user straight back where they were.
   *
   * When every candidate in the slot shares the equipment, it falls back to the ordinary pick rather
   * than leaving the user without an exercise, and says so, so the user can be told.
   */
  async getRepCeilingReplacementAsync(
    e: UiExercise,
    fitnessState: FitnessState,
    plan: FitnessPlan,
    nowLocal: DateTime,
    watcher: DocWatcher,
  ): Promise<{ exerciseId: string; mayUseSameEquipment: boolean }> {
    const utc_now = nowLocal.toUTC();
    // the exercise is moved away for a while, not judged unsuitable, so it is switched, not quarantined
    if (e.stat) {
      ExerciseStat.markSwitched(e.stat, utc_now);
    }
    const currentId = e.exercise && e.exercise._id;
    const suggest = (toAvoid: string[]) =>
      suggestAvailableExercise(fitnessState, plan, e.slot, e.planSubSlot.startIndex, currentId, utc_now, toAvoid);

    // if the equipment is unknown there is nothing to steer away from
    if (!e.equipment?.id || !e.slot) {
      return { exerciseId: suggest([]), mayUseSameEquipment: false };
    }

    // read each candidate's equipment template, so one with no equipment yet can be judged too
    const candidateIds = e.slot.exercises.map(se => se.exerciseId).filter(id => id && id !== currentId);
    const templateIds = new Map<string, string | undefined>();
    for (const id of candidateIds) {
      // the reporting read on purpose: a candidate whose read failed is judged as having no
      // template, the same as above, rather than abandoning the switch (df-c21e11979ef2)
      const candidate = isCustomExerciseById(id) ? undefined : await this.exerciseDb.getGlobalExerciseByIdAsync(id, watcher);
      templateIds.set(id, candidate?.equipmentTemplateId);
    }
    const sameEquipment = exercisesOnSameEquipment(candidateIds, e.equipment, fitnessState, id => templateIds.get(id));

    return pickReplacementAvoidingSameEquipment(suggest, currentId, sameEquipment);
  }

  /**
   * Load exercise by ID and set up UiExercise.exercise and stat.
   * Handles custom vs global exercise loading with error fallback.
   */
  private async setExerciseById(
    uie: UiExercise,
    exerciseId: string,
    plan: FitnessPlan,
    fitnessState: FitnessState,
    userDb: UserDb,
    locale: LocaleSettings,
    nowLocal: DateTime,
    watcher: DocWatcher,
  ): Promise<void> {
    if (isCustomExerciseById(exerciseId)) {
      try {
        uie.exercise = await userDb.getCustomExerciseByIdAsync(exerciseId);
      } catch (err) {
        // only a missing exercise gets a replacement. an unreadable database or a programmer
        // error must travel up: treating it as a deletion would swap the user's exercise and
        // mark the old one switched (df-15c8fd44cf06)
        if (!isPouchNotFound(err)) throw err;
        // custom exercise not found, get replacement
        exerciseId = await this.getReplacementExerciseAsync(uie, 'switched', fitnessState, plan, nowLocal, watcher);
        await this.setExerciseById(uie, exerciseId, plan, fitnessState, userDb, locale, nowLocal, watcher);
      }
    } else {
      // the throwing read, as for the custom branch above: a read that failed must travel,
      // not leave this exercise empty as if it were missing from the catalog (df-c21e11979ef2)
      uie.exercise = await this.exerciseDb.getGlobalExerciseByIdOrThrowAsync(exerciseId, watcher);
    }

    if (!uie.exercise) {
      console.error(`Can't find exercise ${exerciseId}`);
      return;
    }

    // if this actually changed the exercise in the plan (bug fix #915).
    // #4614: skip entirely when the plan has no entry under this id — the user removed this
    // exercise from their program and we are only DISPLAYING a finished session that
    // contained it, restored from the workout log's own snapshot. There is nothing in the
    // plan to change, and writing would put the dropped entry back (see
    // writeSubSlotBackToPlan in workout-detail.ts for the same guard and the full reason).
    //
    // #4939 widened that guard: it now also refuses when the copy being held is a finished
    // session's own snapshot. The entry does still exist in the program there, so the #4614
    // test alone lets the write through — and it would overwrite the program's whole sub-slot
    // with the settings that session was performed under.
    if (uie.canWritePlanSubSlotBackToPlan()
      && uie.exercise._id !== plan.subSlots[uie.logExercise.subSlotId]?.exercise_id) {
      plan.subSlots[uie.logExercise.subSlotId] = deepClone(uie.planSubSlot);
      await userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    }

    uie.stat = this.getOrCreateExerciseStat(fitnessState, uie.exercise._id, locale);
  }

  /**
   * Perform an exercise switch and fully reload the UiExercise in place.
   * This is the shared implementation used by workout-detail and training pages.
   * `chosenBy` says whether the user picked the new exercise or the app did (#6076).
   */
  async performExerciseSwitchAsync(
    uie: UiExercise,
    newExerciseId: string,
    workoutPlanReference: number,
    plan: FitnessPlan,
    fitnessState: FitnessState,
    userDb: UserDb,
    ctx: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
    chosenBy: ExerciseChosenBy,
  ): Promise<void> {
    const oldSubSlotId = uie.logExercise.subSlotId;
    // the exercise the card shows now; a failed switch below that left the card naming any other
    // must not leave this one's previous-session baseline on it (df-f4964c2d2d3c)
    const exerciseIdOnCardBefore = uie.exercise?._id;
    // use canonical plan's exercise_id — the UI exercise may differ if skipAvoidedExercises
    // swapped the displayed exercise (quarantined/switched), or if cross-device sync updated
    // the plan while the workout was open
    const oldExerciseId = plan.subSlots[oldSubSlotId]?.exercise_id ?? uie.exercise._id;

    // if the exercise is not in that workout, the switch below refuses. refuse here instead,
    // BEFORE the custom exercise is tagged with this slot: a failed switch used to leave the
    // tag behind, offering the picked exercise as an alternative for an unrelated one
    // (df-fdac90a65bdb, #4634). same message changeExerciseInWorkoutAsync throws.
    if (!plan.workouts[workoutPlanReference]?.subSlotIds.includes(oldSubSlotId)) {
      throw new Error(`Old subslot ${oldSubSlotId} not found in workout ${workoutPlanReference}`);
    }
    // same for a new exercise this workout already lists: the switch refuses it (#6296), so
    // refuse here, before the tag. the picker refuses it first; this covers a plan that changed
    // between the pick and the return.
    if (workoutAlreadyListsExercise(plan, newExerciseId, plan.workouts[workoutPlanReference], oldSubSlotId)) {
      throw new Error(`Exercise ${newExerciseId} is already in workout ${workoutPlanReference}`);
    }

    // if custom exercise, mark as used in slot. if the exercise has no slot (its entry sits in
    // the `slot/$custom` stand-in, which has no slot document), there is nothing to record: a
    // custom exercise with no slot is exactly what files it under that stand-in again (#6289)
    if (isCustomExerciseById(newExerciseId) && uie.slot) {
      const newExercise = await userDb.getCustomExerciseByIdAsync(newExerciseId);
      if (!newExercise.usedInSlots.includes(uie.slot._id)) {
        newExercise.usedInSlots.push(uie.slot._id);
        await userDb.saveCustomExerciseAsync(newExercise, DatabaseWrapper.getTransactionId());
      }
    }

    // perform the switch
    const result = await changeExerciseInWorkoutAsync(
      workoutPlanReference,
      oldSubSlotId,
      oldExerciseId,
      newExerciseId,
      plan,
      fitnessState,
      this.exerciseDb,
      userDb,
      ctx.nowLocal,
      ctx.watcher,
      chosenBy,
    );

    // save changes
    const transaction = DatabaseWrapper.getTransactionId();
    await userDb.savePlanAsync(transaction);
    await userDb.saveFitnessStateAsync(transaction);

    // update UiExercise basic properties
    uie.logExercise.exerciseId = newExerciseId;
    // remember the id this entry is being renamed AWAY from, when the switch moved it
    // into a different slot and so minted a new one. The save's merge needs it: the
    // document can be holding a pre-switch copy of this same entry under the old id
    // (a canonicalizing read replaces the exercise array with objects from disk), and
    // without this it would keep that copy beside the renamed one and store the user's
    // concluded sets twice. See `mergeScreenExercisesOverStored`.
    if (result.newSubslotId !== oldSubSlotId) {
      uie.retiredSubSlotIds.push(oldSubSlotId);
    }
    uie.logExercise.subSlotId = result.newSubslotId;
    // a different exercise is not the one the user answered a set-volume question
    // about, so let the replacement be judged on its own (#4714)
    delete uie.logExercise.volumeAutoregulationAnswered;
    uie.planSubSlot = deepClone(plan.subSlots[result.newSubslotId]);
    // df-8d1620a9593c: the exercise now lives under a different sub-slot id, so any pointer to the
    // plan's own entry taken before the switch names the entry it was switched AWAY from. Clear it
    // rather than update it: the page re-reads the plan by the current id at every progression
    // write, and a pointer left naming the old entry is the kind of wrong that writes silently.
    uie.livePlanSubSlot = undefined;
    // the new sub-slot came from the live program, so the exercise is no longer standing in for
    // one the program has dropped. Without this the flag stays true for the rest of the page's
    // life and every write-back is silently skipped, including the set-1 progression write
    // (#4614). Reachable: an exercise removed from the program mid-session is kept in the
    // workout by mergeWorkoutExercisesWithPlan, and the user can then switch it.
    uie.planSubSlotMissingFromPlan = false;
    // #4939: and the copy is no longer a finished session's own record either — it was just
    // taken from the live program a few lines above. Left set, every write-back would be
    // silently skipped for the rest of the page's life, the same way it would be if the flag
    // above stayed true. It does mean this exercise draws from the live entry again for the
    // rest of this page session, which is the same trade the line above already makes: the
    // user asked for this switch, and the next load resolves the display from scratch.
    uie.planSubSlotIsHistorical = false;
    // #6428: the coach-change guard was about the exercise being switched away from; the new one
    // takes a fresh snapshot, and leaving the flag set would silently stop every write back
    uie.coachChangedDuringSession = false;
    // this clears less than the temp-swap paths do, and that is correct rather than an
    // oversight: they leave the exercise off-plan for the session and so must clear
    // `planPath`, while this method ends by calling `loadExerciseAsync`, whose
    // `setCurrentPath` assigns `planPath` unconditionally. Clearing it here would be
    // overwritten a few lines later and would say nothing.
    delete uie.reducedTargetWeight_pc;
    delete uie.reducedTargetReps_delta;
    delete uie.layoffWeight_kg;
    delete uie.layoffCut1RM_net_kg;
    delete uie.targetWeight_kg;

    // df-4693ac7f6451: the workout's own copy of the plan entry still describes the exercise the
    // user has just switched AWAY from, and `setCurrentPath` reads that copy in preference to the
    // plan entry whenever one exists — so leaving it here judges the NEW exercise against the OLD
    // exercise's targets. Clear it BEFORE the reload below, so the `setCurrentPath` inside
    // `loadExerciseAsync` reads the new program entry, and re-take it afterwards.
    //
    // On the workout page this now rarely has anything to do: an exercise with a performed set is
    // split before the switch (#5507), and the entry arriving here has had its copy moved to the
    // split-off entry. It still runs for an entry whose copy exists without a performed set.
    const ownPlanCopyBeforeSwitch = uie.logExercise.planSubslotBackup;
    uie.logExercise.planSubslotBackup = undefined;
    // the "as the session left it" copy describes the OLD exercise, and is deliberately NOT re-taken
    // below: a copy of the new exercise's program entry would claim this session trained it, and the
    // layoff restore would hand that back as earned progression. The new exercise's next concluded
    // set takes a fresh one (df-5dbef4889250). If the reload THROWS, the entry still holds the old
    // exercise, so the copy is put back below.
    const afterSessionCopyBeforeSwitch = uie.logExercise.planSubslotAfterSession;
    delete uie.logExercise.planSubslotAfterSession;

    let reloadSucceeded = false;
    try {
      // load exercise and set up stat
      await this.setExerciseById(uie, newExerciseId, plan, fitnessState, userDb, ctx.locale, ctx.nowLocal, ctx.watcher);

      // df-f4964c2d2d3c: the previous session's best set belongs to the exercise switched AWAY from.
      // Cleared before the reload, so `checkEquipment` inside it sizes the new exercise's weight step
      // by the new exercise's own history, and set 1 is never judged against another exercise; the
      // workout page re-reads it for the new exercise. Cleared only NOW that the card names the new
      // exercise: a previous-workout read for the old one still running writes only while the card
      // names the exercise it read, so it can no longer put the old value back. A throw from the
      // line above is handled in the `finally` below.
      delete uie.previousFirstSetStats;

      // fully reload exercise - mutates uie directly including set backlinks
      await this.loadExerciseAsync(uie, ctx, mode);
      reloadSucceeded = true;
    } finally {
      if (!reloadSucceeded && afterSessionCopyBeforeSwitch) {
        uie.logExercise.planSubslotAfterSession = afterSessionCopyBeforeSwitch;
      }
      // `setExerciseById` names the new exercise on the card (or a stand-in for a missing custom
      // one) before its own later writes, which can throw. A card left naming another exercise must
      // not keep the old one's baseline; one still naming the old exercise keeps it
      if (!reloadSucceeded && uie.exercise?._id !== exerciseIdOnCardBefore) {
        delete uie.previousFirstSetStats;
      }
      // re-take the workout's own copy, rather than leaving it deleted. It is created lazily and
      // only by the FIRST conclusion of set 1 (`concludeSet` in workout-detail.ts), which has
      // already happened by the time anybody switches — so a delete would never be undone for the
      // rest of the session, and two protections depend on the copy existing: #3354 pins a finished
      // session's prescribed set count to it, so a later set-count rise in the program cannot
      // manufacture empty sets and flip that session back to unfinished, and #4939 draws a finished
      // session's history from it.
      //
      // Two cases put the ORIGINAL copy back instead of a fresh one, and both leave the workout
      // exactly as it was before the switch rather than with no copy at all — which is the one
      // outcome worse than the stale copy this whole block exists to replace, because nothing
      // recreates it. If the reload THREW: both callers catch that and leave the page alive. If the
      // program holds no entry under the new id: `uie.planSubSlot` is then not a program entry but
      // a display rebuild, and freezing that into the document is what #4614 forbids. The switch
      // sets that flag false a few lines above, so the second case only guards a later change.
      if (ownPlanCopyBeforeSwitch) {
        if (reloadSucceeded && !uie.planSubSlotMissingFromPlan) {
          const snapshot = deepClone(uie.planSubSlot);
          // the date on the copy says WHEN THIS SESSION concluded the exercise
          // (`snapshotConcludedDate`, #4939), so it is carried across unchanged: the session has not
          // changed, only the exercise it holds. Assigned only when the old copy actually had one —
          // taking the new program entry's date instead would name whatever session the NEW exercise
          // last concluded, and would put a date on a copy that never had one, which is exactly what
          // `isUsableLayoffBackup` treats as the mark of established progression.
          if (ownPlanCopyBeforeSwitch.lastTimeConcluded_utc) {
            snapshot.lastTimeConcluded_utc = ownPlanCopyBeforeSwitch.lastTimeConcluded_utc;
          } else {
            delete snapshot.lastTimeConcluded_utc;
          }
          uie.logExercise.planSubslotBackup = snapshot;
        } else {
          uie.logExercise.planSubslotBackup = ownPlanCopyBeforeSwitch;
        }
      }
    }
  }

  /**
   * Perform a temporary exercise swap for this session only.
   * Does NOT modify the plan or fitnessState.oldSubslots — the original exercise remains in the plan.
   * Pre-populates weight/reps from tempSwapHistory if available.
   */
  async performTempExerciseSwitchAsync(
    uie: UiExercise,
    newExerciseId: string,
    fitnessState: FitnessState,
    ctx: WorkoutLoadingContext,
  ): Promise<void> {
    const oldExerciseId = uie.exercise._id;
    const originalSetCount = uie.planSubSlot.defaultVolume_sets;

    // resolve the new exercise
    if (isCustomExerciseById(newExerciseId)) {
      uie.exercise = await this.userDb.getCustomExerciseByIdAsync(newExerciseId);
    } else {
      // a failed read travels as itself rather than as the "cannot find" below (df-c21e11979ef2)
      uie.exercise = await this.exerciseDb.getGlobalExerciseByIdOrThrowAsync(newExerciseId, ctx.watcher);
    }

    if (!uie.exercise) {
      throw new Error(`Cannot find exercise ${newExerciseId} for temp swap`);
    }

    // resolve the slot for the new exercise
    uie.slot = await this.exerciseDb.slotForIdAsync(uie.planSubSlot.slotId, ctx.watcher);

    // mark it as a temp swap
    uie.isTempSwap = true;
    uie.tempSwapOriginalExerciseId = oldExerciseId;
    uie.logExercise.isTempSwap = true;
    uie.logExercise.tempSwapOriginalExerciseId = oldExerciseId;
    uie.logExercise.exerciseId = newExerciseId;
    // as in performExerciseSwitchAsync above: a different exercise is not the one the
    // user answered a set-volume question about, so let it be judged on its own (#4714)
    delete uie.logExercise.volumeAutoregulationAnswered;

    // clear plan-related progression (no plan modification)
    this.clearTempSwapProgression(uie);

    // get or create the exercise stat, and give it a weight unit
    const stat = this.getOrCreateExerciseStat(fitnessState, newExerciseId, ctx.locale);
    uie.stat = stat;

    // check equipment for the new exercise
    uie.checkEquipment(fitnessState, this.userDb, ctx.locale, this.logging);

    // synchronize units from the equipment config
    this.syncStatUnitFromEquipment(uie, stat);

    // build simple sets (no plan-based logic, always empty weight/reps)
    const sets: UiSet[] = [];
    uie.logExercise.sets = [];

    for (let i = 0; i < originalSetCount; i++) {
      const uis = new UiSet();
      const logSet: Log_WorkoutSet = {
        state: LogRecordState.Scheduled,
        setType: i === 0 ? SetType.Regular1st : SetType.Followup,
        stats: {},
        setSuccess: undefined,
        preStatus: [],
        postStatus: [],
      } as Log_WorkoutSet;

      uis.logSet = logSet;
      uis.uiExercise = uie;
      uie.logExercise.sets.push(logSet);
      sets.push(uis);
    }

    uie.sets = sets;

    // remember the myo-reps setting these sets were built with. The sets above are built by
    // hand rather than through `buildAndAssignSets`, so without this line the value recorded
    // for the exercise that was swapped OUT stays behind. `clearTempSwapProgression` has
    // already cleared `planPath`, so the answer here is the one that matches these sets —
    // and the page's check in `ionViewDidEnter` would otherwise read the leftover value as
    // "the setting changed" and rebuild this exercise on the next entry, closing its open
    // set (df-a7edc07db76a, the same symptom df-7499557dbb0a removed from the fresh load).
    uie.builtWithMyoReps = this.willUseMyoReps(uie, ctx.declaredState, fitnessState);

    // generate the badges and the header line (declaredState is not needed for the badges —
    // the temp swap badge returns early)
    this.applyBadgesAndDescription(uie, ctx.declaredState, fitnessState, WorkoutLoadMode.Current, ctx.locale);
  }

  /**
   * Generate program overview data for display in training program summary views.
   * Returns workout list with exercises and muscle volume data.
   */
  async generateProgramOverviewAsync(
    trainingData: TrainingProgramData,
    watcher?: DocWatcher,
  ): Promise<ProgramOverviewData> {
    const tempWatcher = watcher ? null : this.exerciseDb.database.createDocWatcher(() => {});
    const effectiveWatcher = watcher || tempWatcher;
    try {
      return await this.buildProgramOverviewAsync(trainingData, effectiveWatcher);
    } finally {
      // in a FINALLY: the reads inside buildProgramOverviewAsync can throw — a database that
      // is shutting down or recovering refuses every read — and a `destroy()` placed after
      // the call would never run, leaving this watcher registered for the rest of the
      // session. Only the watcher this method created is destroyed; a caller-supplied one
      // belongs to the caller.
      tempWatcher?.destroy();
    }
  }

  // The body of generateProgramOverviewAsync, split out only so the watcher above can be
  // released in a `finally` without reindenting the whole method. It does NOT own the
  // watcher: the caller supplies a live one and is responsible for destroying it, so this
  // must not be called on its own.
  private async buildProgramOverviewAsync(
    trainingData: TrainingProgramData,
    effectiveWatcher: DocWatcher,
  ): Promise<ProgramOverviewData> {
    const { fitnessPlan, declaredState, fitnessState } = trainingData;

    const workouts: WorkoutsOverviewData = {
      unavailable: 'Workouts unavailable until you generate the plan',
      expanded: false,
      allLetters: '',
      overallSets: '',
      overallExercises: '',
      list: []
    };

    // if workouts exist in the plan
    if (fitnessPlan.workouts && fitnessPlan.workouts.length > 0) {
      workouts.unavailable = undefined;
      workouts.letters = '';
      let minExercises = fitnessPlan.workouts[0].subSlotIds.length;
      let maxExercises = minExercises;
      let minSets = sumBy(fitnessPlan.workouts[0].subSlotIds, sId => fitnessPlan.subSlots[sId].defaultVolume_sets);
      let maxSets = minSets;

      // load custom exercises once (not per workout)
      let allCustomExercises = await this.userDb.getAllCustomExercisesAsync();

      // collect all unique exercise IDs across all workouts for batch loading
      const allExerciseIds = new Set<string>();
      for (let wo of fitnessPlan.workouts) {
        for (let sId of wo.subSlotIds) {
          const exerciseId = fitnessPlan.subSlots[sId]?.exercise_id;
          if (exerciseId) allExerciseIds.add(exerciseId);
        }
      }

      // pre-fetch all global exercises in parallel. the reporting read on purpose: this only
      // fills an overview, and one unreadable exercise must not blank it (df-c21e11979ef2)
      const exerciseMap = new Map<string, any>();
      await Promise.all([...allExerciseIds].map(async (id) => {
        const exercise = await this.exerciseDb.getGlobalExerciseByIdAsync(id, effectiveWatcher)
          || allCustomExercises.find(e => e._id === id);
        if (exercise) exerciseMap.set(id, exercise);
      }));

      for (let wo of fitnessPlan.workouts) {
        minExercises = Math.min(minExercises, wo.subSlotIds.length);
        maxExercises = Math.max(minExercises, wo.subSlotIds.length);
        let sets = sumBy(wo.subSlotIds, sId => fitnessPlan.subSlots[sId].defaultVolume_sets);
        minSets = Math.min(minSets, sets);
        maxSets = Math.max(minSets, sets);
        workouts.letters += wo.name;

        let exerciseInfos: ExerciseOverviewInfo[] = [];

        for (let i = 0; i < wo.subSlotIds.length; i++) {
          let sId = wo.subSlotIds[i];
          let exerciseId = fitnessPlan.subSlots[sId] && fitnessPlan.subSlots[sId].exercise_id;
          let exercise = exerciseId && exerciseMap.get(exerciseId);
          let useMyoReps = exerciseUsesMyoReps(exercise, fitnessPlan.subSlots[sId].defaultVolume_sets, declaredState, fitnessState);
          let useRMTesting = exerciseUsesRMTesting(exercise, declaredState, fitnessState);

          // if all paths have target_reps < 8
          if (fitnessPlan.subSlots[sId].exercisePaths.every(path => path.target_reps && path.target_reps < 8)) {
            useMyoReps = false;
          }

          const defaultVolume_sets = fitnessPlan.subSlots[sId].defaultVolume_sets;
          let numSets = useMyoReps ? getMyoSetsFromSets(defaultVolume_sets).length : defaultVolume_sets;

          exerciseInfos.push({
            name: exercise.shortname || exercise.name,
            sets: numSets,
            useMyoReps,
            rmTesting: useRMTesting
          });
        }

        workouts.list.push({
          letter: wo.name,
          exercises: exerciseInfos,
        });
      }

      workouts.allLetters = fitnessPlan.workouts.map(w => w.name).join('');
      workouts.overallSets = minSets === maxSets ? `${minSets}` : `${minSets}-${maxSets}`;
      workouts.overallExercises = minExercises === maxExercises ? `${minExercises}` : `${minExercises}-${maxExercises}`;

      let activeExercises = [];
      for (let ssId in fitnessPlan.subSlots) {
        let ss = fitnessPlan.subSlots[ssId];
        activeExercises.push(ss.exercise_id);
      }

      // count active vs inactive exercises
      let active = activeExercises.length;
      let inactive = 0;
      for (let eId in fitnessState.exerciseStats) {
        if (!activeExercises.some(e => e.id === eId)) {
          inactive++;
        }
      }

      workouts.exercisesActiveInactive = `${active} active, ${inactive} inactive`;
    }

    let lowerBodyMuscles: MuscleVolumeEntry[] = [];
    let upperBodyMuscles: MuscleVolumeEntry[] = [];

    if (fitnessPlan.workouts && fitnessPlan.workouts.length > 0) {
      const workoutsPerWeek = getWorkoutsPerWeek(declaredState);

      const inputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, effectiveWatcher);
      reportOrphanSubSlotIds(fitnessPlan, this.logging, 'workout-loader.assembleWorkoutDetailsAsync');
      // this call site does not collect uncounted sub-slots, so the out-array argument is skipped
      let muscleVolume: PerMuscleVolume = getMuscleVolumeForPlan(
        fitnessPlan, workoutsPerWeek, inputs.muscleFactorOverrides, undefined, inputs.usesMyoRepsBySubSlotId,
      );

      const rows = buildMuscleVolumeRows(muscleVolume);
      lowerBodyMuscles = rows.lowerBodyMuscles;
      upperBodyMuscles = rows.upperBodyMuscles;
    }

    return { workouts, lowerBodyMuscles, upperBodyMuscles };
  }
}
